/**
 * Arlo & Co — Product Options
 * Reads window.ArloProductConfig.productOptions[handle] and injects
 * dropdown / buttons / tiles / checkbox fields into the product form,
 * submitted as Shopify line item properties (properties[Label]).
 *
 * Requires: arlo-product-config.js loaded first.
 */

(function () {
  'use strict';

  // Guard against double-execution: arlo-options.js is loaded by both
  // canvas-capture.liquid and uploadcare-fields.liquid. The second load must
  // be a no-op — otherwise buildFields() resets fpFontHidden to the default
  // font, wiping the user's font selection before cart submit.
  if (window._arloOptionsLoaded) return;
  window._arloOptionsLoaded = true;

  // Mark this page as Arlo-managed the instant this script is parsed.
  // Runs synchronously so CSS rules scoped to html.arlo-managed apply
  // before the browser paints — no flash of old option blocks.
  document.documentElement.classList.add('arlo-managed');

  // Round canvas-png text boxes: gap kept clear between the text and the circle
  // edge, as a fraction of the DIAMETER on each side (0.05 = 5% safe zone).
  var CIRCLE_SAFE_ZONE = 0.05;

  // 2026-10-13: page-wide price selectors ([data-price], .money …) also match the
  // product TILES further down the page ("You may also like"), the cart drawer,
  // search and menus — each showing a DIFFERENT product's price. Writing into
  // them put this product's price on every recommended product. Only ever touch
  // prices that belong to the main product.
  var OTHER_PRODUCT_AREAS = '.product-item, .quick-cart, .cross-sells, .product-card-mini, .quick-search, .predictive-search, .meganav, .mobile-menu';
  function mainPriceEls(sel) {
    return [].filter.call(document.querySelectorAll(sel), function (el) {
      return !(el.closest && el.closest(OTHER_PRODUCT_AREAS));
    });
  }

  // Badge label for an option button value: percentage text as authored, or a
  // computed "Save $X" when bv.badgeStyle === 'dollar'. Dollar savings round down
  // to the nearest 50c to match the page-price badge.
  function arloOptBadgeLabel(bv, baseCents) {
    if (!bv.discountBadge) return '';
    // Dollar is the default; only show the literal percentage when explicitly chosen.
    if (bv.badgeStyle === 'percent') return bv.discountBadge;
    var m = bv.discountBadge.match(/(\d+(?:\.\d+)?)/);
    var pct = m ? parseFloat(m[1]) : null;
    var savings = null;
    if (bv.compareAtPrice && bv.salePrice) {
      savings = Math.round((parseFloat(bv.compareAtPrice) - parseFloat(bv.salePrice)) * 100);
    } else if (pct != null) {
      // Pre-discount total this button represents:
      //   quantity buttons → base product price × parentQty
      //   explicit price   → compareAt or price
      var fullCents = (bv.parentQty && baseCents) ? baseCents * bv.parentQty
                    : bv.compareAtPrice ? Math.round(parseFloat(bv.compareAtPrice) * 100)
                    : bv.price ? Math.round(parseFloat(bv.price) * 100)
                    : (baseCents || null);
      if (fullCents != null) savings = Math.round(fullCents * pct / 100);
    }
    if (savings != null && savings > 0) {
      var dollars = (Math.floor(savings / 50) * 50) / 100;
      return 'Save $' + (dollars % 1 === 0 ? dollars : dollars.toFixed(2));
    }
    return bv.discountBadge;
  }

  // ── Step coordinator ──────────────────────────────────────────────────────────
  // Shared global across all three Arlo scripts (options / variant-buttons / photo-uploader).
  // Each script registers its badge <span> elements here. After ALL DOMContentLoaded
  // handlers have fired (setTimeout 0), badges are sorted by DOM position and numbered
  // 1, 2, 3... in visual order. No fragile config-handle lookups or DOM timing hacks.
  if (!window._arloStepCoord) {
    window._arloStepCoord = {
      _badges: [],
      _scheduled: false,
      register: function (el) {
        this._badges.push(el);
        if (!this._scheduled) {
          this._scheduled = true;
          var self = this;
          setTimeout(function () {
            self._badges.sort(function (a, b) {
              // Node.DOCUMENT_POSITION_FOLLOWING === 4
              return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
            });
            // Only show step numbers when there are 2+ steps
            if (self._badges.length > 1) {
              self._badges.forEach(function (b, i) { b.textContent = i + 1; });
            } else {
              self._badges.forEach(function (b) { b.style.display = 'none'; });
            }
          }, 0);
        }
      }
    };
  }

  var CONTAINER_ID = 'arlo-options-container';

  // ── Switch product image (replicates Artisan theme's Ca() function) ───────────
  function arloSwitchProductImage(imageIndex) {
    // console.log('[ArloOptions] arloSwitchProductImage called with index:', imageIndex);
    var photoContainers = document.querySelectorAll(
      '.product__media-container.above-mobile, .product__media-container.below-mobile'
    );
    // console.log('[ArloOptions] photoContainers found:', photoContainers.length);
    photoContainers.forEach(function (container) {
      var wrappers = container.querySelectorAll('[data-product-media-wrapper]');
      wrappers.forEach(function (w, i) {
        if (i === imageIndex) {
          w.classList.remove('hidden');
        } else {
          w.classList.add('hidden');
        }
      });
    });

    // Update active state on all thumbnail buttons
    document.querySelectorAll('[data-product-thumbnail]').forEach(function (t) {
      var isActive = String(t.dataset.thumbnailIndex) === String(imageIndex);
      t.classList.toggle('active', isActive);
    });

    // Tell the Artisan thumbnail strip to scroll to this slide
    var scrollerEl = document.querySelector('[data-scroller-id]');
    var scrollerId = scrollerEl && scrollerEl.dataset.scrollerId;
    if (scrollerId) {
      document.dispatchEvent(new CustomEvent(
        'scroll-slider-' + scrollerId + ':go-to-slide',
        { detail: { slideIndex: String(imageIndex) } }
      ));
    }
  }

  // ── Get options for current product ──────────────────────────────────────────
  function getOptions() {
    var config = window.ArloProductConfig;
    if (!config || !config.productOptions) return null;
    var handle = (window.ShopifyAnalytics &&
                  window.ShopifyAnalytics.meta &&
                  window.ShopifyAnalytics.meta.product &&
                  window.ShopifyAnalytics.meta.product.handle) || null;
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m ? m[1] : null;
    }
    if (!handle) return null;
    return config.productOptions[handle] || null;
  }

  // ── Linked product price tracking ─────────────────────────────────────────────
  var _linkedExtraCents = 0;
  var _buttonBasePriceCents = null;  // captured once before first button-option price override
  var _baseCompareCents = null;      // the product's own Shopify compare-at (cents, 0 = none), same capture
  var _buttonPriceActive = false;    // true when a button option is overriding the page price

  // ── Live variant price cache ───────────────────────────────────────────────────
  // Fetches current prices from Shopify storefront so stale config prices are ignored.
  var _liveVariantPrices = {};  // variantId (string) → price in dollars (number) or null

  function prefetchLinkedVariantPrices(opts) {
    var ids = [];
    opts.forEach(function (opt) {
      if (opt.linkedVariantId) ids.push(String(opt.linkedVariantId));
      if (opt.buttonValues) {
        opt.buttonValues.forEach(function (bv) {
          if (bv.linkedVariantId) ids.push(String(bv.linkedVariantId));
        });
      }
    });
    // Deduplicate and fetch
    ids.filter(function (id, i, a) { return a.indexOf(id) === i; }).forEach(function (id) {
      if (_liveVariantPrices[id] !== undefined) return;
      _liveVariantPrices[id] = null; // mark as pending so we don't double-fetch
      fetch('/variants/' + id + '.json')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          _liveVariantPrices[id] = data.variant ? parseFloat(data.variant.price) : null;
        })
        .catch(function () { _liveVariantPrices[id] = null; });
    });
  }

  function getLiveLinkedPrice(variantId) {
    // Returns live price in dollars, or null if not yet fetched / not available
    return _liveVariantPrices[String(variantId)] !== undefined
      ? _liveVariantPrices[String(variantId)]
      : null;
  }

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount)
              .replace('{{amount_no_decimals}}', Math.round(cents / 100))
              .replace('{{amount_with_comma_separator}}', amount)
              .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }

  function applyLinkedPriceDelta(deltaCents) {
    _linkedExtraCents += deltaCents;
    mainPriceEls('[data-price]').forEach(function (el) {
      if (!el.dataset.arloBaseCents) {
        // Parse the current displayed price to get base cents (first time only)
        var raw = el.textContent.replace(/[^0-9.]/g, '');
        el.dataset.arloBaseCents = Math.round(parseFloat(raw) * 100) || 0;
      }
      var base = parseInt(el.dataset.arloBaseCents, 10);
      el.innerHTML = formatMoney(base + _linkedExtraCents);
    });
  }

  // ── Button-option page price (direct SET, not delta) ─────────────────────────
  var _PRICE_SELECTORS = [
    '[data-product-price]',
    '.price__current .money',
    '.price-item--regular',
    '.product__price .money',
    '.product-single__price',
    '[data-price]',
  ].join(',');

  // 2026-10-11: the theme wraps EVERY price in span.money — including the
  // struck-through compare-at inside <s data-compare-price>, which comes first.
  // During a Shopify sale that made the set buttons read the OLD price as the
  // unit price (2 sets = 2 × $49 instead of 2 × $39) and write their total
  // into the strike-through too. Never read or write prices inside these.
  function _inComparePrice(el) {
    return !!(el.closest && el.closest('[data-compare-price], s, .price__compare, .price__was'));
  }
  // The theme's own strike-through as first rendered = the current variant's
  // Shopify compare-at. Read once, before any button writes into it.
  function _captureBaseCompare() {
    if (_baseCompareCents !== null) return;
    var el = mainPriceEls('[data-compare-price]')[0];
    var raw = el ? el.textContent.replace(/[^0-9.]/g, '') : '';
    _baseCompareCents = raw ? (Math.round(parseFloat(raw) * 100) || 0) : 0;
  }
  function _captureBasePrice() {
    _captureBaseCompare();
    if (_buttonBasePriceCents !== null) return;
    // The current variant's real price (cents) from Shopify's page data first.
    try {
      var vs = window.ShopifyAnalytics.meta.product.variants, vid = _currentVariantId();
      for (var i = 0; i < vs.length; i++) {
        if (String(vs[i].id) === vid || vs.length === 1) { _buttonBasePriceCents = vs[i].price; return; }
      }
    } catch (e) {}
    var el = [].filter.call(mainPriceEls(_PRICE_SELECTORS), function (x) { return !_inComparePrice(x); })[0];
    if (el) {
      var raw = el.textContent.replace(/[^0-9.]/g, '');
      _buttonBasePriceCents = Math.round(parseFloat(raw) * 100) || 0;
    }
  }

  // ── Sale mode: live Shopify markdown for quantity-set buttons (2026-10-07) ──
  // During a sale the "1 / 2 / 3 sets" buttons drop the theme's own 15% and
  // show the Shopify compare-at sale instead: now = qty × live price, was =
  // qty × live compare-at, with a real "Save" tag (kept visible in sale mode via
  // arlo-vb-badge--shopify). Only fetched when sale mode is on.
  var _liveVariantMap = null;
  function _currentVariantId() {
    var f = document.querySelector('form[action*="/cart/add"]');
    var i = f && f.querySelector('[name="id"]');
    return i && i.value ? String(i.value) : null;
  }
  function _liveUnitCompareCents() {
    if (!_liveVariantMap) return 0;
    var v = _liveVariantMap[_currentVariantId()];
    if (!v) {
      var ids = Object.keys(_liveVariantMap);
      if (ids.length === 1) v = _liveVariantMap[ids[0]];
    }
    return (v && v.compare_at_price > v.price) ? v.compare_at_price : 0;
  }
  function _saleSavingLabel(wasCents, nowCents, style) {
    var save = wasCents - nowCents;
    if (!(save > 0)) return '';
    if (style === 'percent') {
      var p = Math.floor(save / wasCents * 100);
      return p > 0 ? 'Save ' + p + '%' : '';
    }
    var d = Math.floor(save / 50) * 50 / 100;
    return d > 0 ? 'Save $' + (d % 1 === 0 ? d : d.toFixed(2)) : '';
  }
  function _isQtySetButton(bv) {
    return !!(bv && bv.parentQty && !bv.price && !bv.salePrice);
  }
  function _loadLiveVariantsForSale() {
    if (!(window.ArloSaleMode && window.ArloSaleMode.active) || !window.fetch) return;
    var handle = getCurrentHandle();
    if (!handle) return;
    fetch('/products/' + handle + '.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var m = {};
        (d.variants || []).forEach(function (v) { m[String(v.id)] = v; });
        _liveVariantMap = m;
        _captureBasePrice();
        var unitWas = _liveUnitCompareCents() || _baseCompareCents;
        document.querySelectorAll('.arlo-opt-button').forEach(function (b) {
          var bv = b._arloBv;
          if (!_isQtySetButton(bv) || !_buttonBasePriceCents) return;
          var label = unitWas ? _saleSavingLabel(unitWas * bv.parentQty, _buttonBasePriceCents * bv.parentQty, bv.badgeStyle) : '';
          var badge = b.querySelector('.arlo-vb-badge');
          if (!label) { if (badge) badge.classList.remove('arlo-vb-badge--shopify'); return; }
          if (!badge) { badge = document.createElement('span'); badge.className = 'arlo-vb-badge'; b.appendChild(badge); }
          badge.textContent = label;
          badge.classList.add('arlo-vb-badge--shopify');
        });
        var active = document.querySelector('.arlo-opt-button.active');
        if (active && _isQtySetButton(active._arloBv)) setOptButtonPagePrice(active._arloBv);
      })
      .catch(function () {});
  }

  function setOptButtonPagePrice(bv) {
    // NB: bv.hidePrice hides the price on the button CARD only (handled where the
    // card price row is built). It must NOT stop the main page price from updating —
    // a button can hide its own price yet still change the product's displayed price.
    _captureBasePrice();

    var priceCents, compareAtCents = 0;
    var shopifyCompare = false; // compareAtCents is a real Shopify markdown, not the theme's own discount

    if (bv.price || bv.salePrice) {
      // ── Explicit price mode: admin-entered price overrides everything ──────────
      priceCents = Math.round(parseFloat(bv.salePrice || bv.price) * 100);
      if (bv.compareAtPrice) compareAtCents = Math.round(parseFloat(bv.compareAtPrice) * 100);
    } else if (bv.parentQty && _buttonBasePriceCents) {
      // ── Auto mode: base price × qty, then apply discount badge ────────────────
      var multipliedCents = _buttonBasePriceCents * bv.parentQty;
      if (bv.discountBadge) {
        var pctMatch = bv.discountBadge.match(/\d+/);
        if (pctMatch) {
          var pct = parseInt(pctMatch[0]) / 100;
          // Round to nearest $0.05 for clean prices
          priceCents = Math.round(multipliedCents * (1 - pct) / 5) * 5;
          compareAtCents = multipliedCents;
        } else {
          priceCents = multipliedCents;
        }
      } else {
        priceCents = multipliedCents;
      }
    } else if (bv.addonPrice && _buttonBasePriceCents !== null) {
      // ── Add-on mode: base price + linked product's price ──────────────────────
      // Keep the product's own Shopify markdown visible: "was" = its compare-at
      // + the add-on (2026-10-11; before, adding a gift bag dropped the strike).
      var addonCents = Math.round(parseFloat(bv.addonPrice) * 100);
      priceCents = _buttonBasePriceCents + addonCents;
      compareAtCents = _baseCompareCents > _buttonBasePriceCents ? _baseCompareCents + addonCents : 0;
      shopifyCompare = true;
    } else {
      // No price config — leave page price alone
      restoreOptButtonPagePrice();
      return;
    }

    // Sale mode: hide the theme's own discount. Show the pre-discount price and
    // drop the strikethrough so the theme isn't advertising a saving on top of
    // a Shopify sale. (Badges are hidden via CSS.)
    var saleSaving = '';
    if (window.ArloSaleMode && window.ArloSaleMode.active) {
      if (_isQtySetButton(bv) && _buttonBasePriceCents) {
        // Sets: qty × live (sale) price, struck through against qty × live
        // compare-at when Shopify has a markdown — the sale's real saving.
        priceCents = _buttonBasePriceCents * bv.parentQty;
        var unitWas = _liveUnitCompareCents() || _baseCompareCents;
        compareAtCents = unitWas > _buttonBasePriceCents ? unitWas * bv.parentQty : 0;
        saleSaving = compareAtCents ? _saleSavingLabel(compareAtCents, priceCents, bv.badgeStyle) : '';
      } else if (!shopifyCompare) {
        if (compareAtCents) priceCents = compareAtCents;
        compareAtCents = 0;
      }
    }

    _buttonPriceActive = true;

    // Shopify moneyFormat may contain HTML (e.g. <span class=money>$X</span>)
    // Strip tags to get plain text so we can safely set textContent
    function _plainMoney(cents) {
      return formatMoney(cents).replace(/<[^>]+>/g, '');
    }

    var plainPrice = _plainMoney(priceCents);
    mainPriceEls(_PRICE_SELECTORS).forEach(function (el) {
      if (_inComparePrice(el)) return; // the strike-through is handled below
      if (/[\$£€¥]/.test(el.textContent) || el.hasAttribute('data-product-price') || el.hasAttribute('data-price')) {
        // If the element has an inner .money span, update that; otherwise the element itself
        var moneyEl = el.querySelector('.money') || el;
        moneyEl.textContent = plainPrice;
      }
    });
    // The theme's own strike-through (<s data-compare-price>): show this
    // button's "was" total, or clear it — a leftover single-unit compare-at
    // next to a multi-set total would be wrong.
    mainPriceEls('[data-compare-price]').forEach(function (el) {
      var moneyEl = el.querySelector('.money') || el;
      moneyEl.textContent = compareAtCents ? _plainMoney(compareAtCents) : '';
      el.style.display = compareAtCents ? '' : 'none';
    });
    // Compare-at price
    if (compareAtCents) {
      var plainCompare = _plainMoney(compareAtCents);
      mainPriceEls('.price__compare .money, [data-compare-at-price], .price__was .money, .price-item--sale').forEach(function (el) {
        el.textContent = plainCompare;
        var wrap = el.closest('[style*="display: none"]') || el.closest('.price__compare');
        if (wrap && wrap.style.display === 'none') wrap.style.display = '';
      });
    }
    // Discount badge — inject once, reuse; matches .arlo-vb-badge style exactly
    var badge = document.getElementById('arlo-btn-opt-badge') || (function () {
      var b = document.createElement('span');
      b.id = 'arlo-btn-opt-badge';
      b.className = 'arlo-vb-badge';
      b.style.cssText = 'display:inline-block;margin-top:6px';
      var priceEl = document.querySelector('[data-product-price],.price__current,.product__price');
      if (priceEl) priceEl.appendChild(b);
      return b;
    })();
    if (saleSaving) {
      // Sale: the real Shopify markdown — stays visible in sale mode.
      badge.textContent = saleSaving;
      badge.classList.add('arlo-vb-badge--shopify');
      badge.style.display = 'inline-block';
    } else if (bv.discountBadge) {
      // Show dollar saving if we have both prices; otherwise fall back to badge text
      var savingsCents = compareAtCents - priceCents;
      var savingsDisplay = (function() {
        var rounded = Math.floor(savingsCents / 50) * 50; // round DOWN to nearest 50c
        var dollars = rounded / 100;
        return '$' + (dollars % 1 === 0 ? dollars : dollars.toFixed(2));
      })();
      badge.textContent = (compareAtCents && savingsCents > 0)
        ? 'Save ' + savingsDisplay
        : bv.discountBadge;
      // Shared with arlo-variant-buttons.js; this is the theme's own saving,
      // not a Shopify markdown, so sale mode must be able to hide it.
      badge.classList.remove('arlo-vb-badge--shopify');
      badge.style.display = 'inline-block';
    } else {
      badge.style.display = 'none';
    }
  }

  function restoreOptButtonPagePrice() {
    if (!_buttonPriceActive) return;
    _buttonPriceActive = false;
    if (_buttonBasePriceCents !== null) {
      var plainPrice = formatMoney(_buttonBasePriceCents + _linkedExtraCents).replace(/<[^>]+>/g, '');
      mainPriceEls(_PRICE_SELECTORS).forEach(function (el) {
        if (_inComparePrice(el)) return;
        if (/[\$£€¥]/.test(el.textContent) || el.hasAttribute('data-product-price') || el.hasAttribute('data-price')) {
          var moneyEl = el.querySelector('.money') || el;
          moneyEl.textContent = plainPrice;
        }
      });
    }
    // Put the theme's strike-through back to the product's own compare-at
    // (button prices may have changed or hidden it).
    if (_baseCompareCents !== null) {
      mainPriceEls('[data-compare-price]').forEach(function (el) {
        var moneyEl = el.querySelector('.money') || el;
        var show = _baseCompareCents > (_buttonBasePriceCents || 0);
        moneyEl.textContent = show ? formatMoney(_baseCompareCents + _linkedExtraCents).replace(/<[^>]+>/g, '') : '';
        el.style.display = show ? '' : 'none';
      });
    }
    // Hide the injected badge
    var badge = document.getElementById('arlo-btn-opt-badge');
    if (badge) badge.style.display = 'none';
  }

  // ── Product image switcher ────────────────────────────────────────────────────
  function setProductImageByIndex(oneBased) {
    var idx = (oneBased || 1) - 1; // convert to 0-based
    // Try Artisan/Dawn media gallery thumbnails
    var thumbs = document.querySelectorAll(
      '.product-gallery__thumbnails li, .product__media-list .product__media-item, ' +
      '.thumbnails-slider__item, [data-media-type] .thumbnail, ' +
      '.product-single__thumbnails .product-single__thumbnail-item'
    );
    if (thumbs.length > idx) {
      var t = thumbs[idx];
      var link = t.querySelector('a, button') || t;
      link.click();
      return;
    }
    // Fallback: look for a Swiper/Slick slide and simulate swipe
    var slides = document.querySelectorAll(
      '.product-gallery__slide, .product__media-item, .slick-slide[data-slick-index], .swiper-slide'
    );
    if (slides.length > idx) {
      slides[idx].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }

  // Reset base price cache whenever the theme swaps variant (so our delta applies to fresh base)
  document.addEventListener('product:variant-change', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant) return;
    // Reset button-option base price so next override captures the new variant price
    _buttonBasePriceCents = variant.price;
    _baseCompareCents = variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
    _buttonPriceActive = false;
    mainPriceEls('[data-price]').forEach(function (el) {
      el.dataset.arloBaseCents = variant.price;
      el.innerHTML = formatMoney(variant.price + _linkedExtraCents);
    });
  });

  // ── Inject styles ─────────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('arlo-options-styles')) return;
    var s = document.createElement('style');
    s.id = 'arlo-options-styles';
    s.textContent = `
      #arlo-options-container { margin-bottom: 20px; }

      /* Each option block */
      .arlo-opt { margin-bottom: 16px; }

      /* Shared label above every option type */
      .arlo-opt-label {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        margin-bottom: 8px;
        color: var(--color-foreground, #111);
        font-size: 12px; letter-spacing: 0.08em;
      }
      .arlo-opt-step-badge {
        width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
        background: var(--color-button-primary-background, #111);
        color: var(--color-button-primary-text, #fff);
        font-size: 13px; font-weight: 700;
        display: flex; align-items: center; justify-content: center;
      }
      .arlo-opt-required { color: var(--color-error, #c0392b); margin-left: 2px; }

      /* ── Dropdown ── */
      .arlo-opt-select {
        width: 100%;
        padding: 10px 14px;
        font-size: 14px;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        appearance: none;
        -webkit-appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 14px center;
        cursor: pointer;
        font-family: inherit;
      }
      .arlo-opt-select:focus { outline: 2px solid var(--color-foreground, #111); outline-offset: 2px; }
      .arlo-opt-select.arlo-opt-error { border-color: var(--color-error, #c0392b); }

      /* ── Buttons — horizontal scroll row (few items fill, many scroll) ── */
      .arlo-opt-buttons {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 10px;
        align-items: stretch;            /* all cards match the tallest — no jumping */
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 6px;             /* room for the scrollbar */
      }
      .arlo-opt-buttons > .arlo-vb-item {
        flex: 1 0 140px;                 /* grow to fill when few; min 140px then scroll */
        scroll-snap-align: start;
      }
      .arlo-opt-buttons::-webkit-scrollbar { height: 6px; }
      .arlo-opt-buttons::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }

      /* .arlo-vb-btn from arlo-variant-buttons.js provides the card base.
         Only override active + error states here. */
      .arlo-opt-buttons .arlo-vb-title { text-align: center; }
      .arlo-opt-buttons .arlo-vb-desc { text-align: center; font-size: 10px; color: var(--color-foreground-secondary, #666); line-height: 1.3; margin-top: 1px; }
      .arlo-opt-button.active {
        box-shadow: inset 0 0 0 2.5px var(--color-button-primary-background, #111) !important;
        background-color: color-mix(in srgb, var(--color-button-primary-background, #111) 15%, transparent) !important;
      }
      @supports not (color: color-mix(in srgb, red 15%, transparent)) {
        .arlo-opt-button.active { background-color: rgba(0,0,0,0.07) !important; }
      }
      .arlo-opt-buttons.arlo-opt-error .arlo-opt-button:not(.active) {
        box-shadow: inset 0 0 0 1.5px var(--color-error, #c0392b) !important;
      }

      /* ── Tiles — horizontal scroll row (few items fill, many scroll) ── */
      .arlo-opt-tiles {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 10px;
        align-items: stretch;            /* all tiles match the tallest — no jumping */
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 6px;
      }
      .arlo-opt-tiles::-webkit-scrollbar { height: 6px; }
      .arlo-opt-tiles::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }

      .arlo-opt-tile {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        padding: 6px 6px 8px;
        gap: 6px;
        border: 1.5px solid var(--color-border, #d1d1d1);
        border-radius: var(--radius, 6px);
        background: var(--color-background, #fff);
        cursor: pointer;
        font-family: inherit;
        transition: border-color 0.15s, box-shadow 0.15s;
        box-sizing: border-box;
        flex: 1 0 104px;                 /* grow to fill when few; min 104px then scroll */
        scroll-snap-align: start;
      }
      .arlo-opt-tile:hover { border-color: var(--color-foreground, #111); }
      .arlo-opt-tile.active {
        border-color: var(--color-foreground, #111);
        border-width: 2px;
        box-shadow: 0 0 0 1px var(--color-foreground, #111);
      }
      .arlo-opt-tile-swatch {
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 3px;
        background-size: cover;
        background-position: center;
      }
      .arlo-opt-tile-label {
        font-size: 13px;
        font-weight: 600;
        color: var(--color-foreground-secondary, #666);
        text-align: center;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 1.2;
      }
      .arlo-opt-tiles.arlo-opt-error .arlo-opt-tile {
        border-color: var(--color-error, #c0392b);
      }

      /* ── Checkbox ── */
      .arlo-opt-checkbox-wrap {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        padding: 10px 14px;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
      }
      .arlo-opt-checkbox-wrap.arlo-opt-error { border-color: var(--color-error, #c0392b); }
      .arlo-opt-checkbox {
        width: 18px; height: 18px;
        accent-color: var(--color-foreground, #111);
        cursor: pointer;
        flex-shrink: 0;
      }
      .arlo-opt-checkbox-label { font-size: 14px; color: var(--color-foreground, #111); }

      /* ── Monogram input ── */
      .arlo-opt-mono-row {
        display: flex;
        gap: 8px;
        align-items: stretch;
        position: relative;
      }
      .arlo-opt-mono-required {
        position: absolute;
        bottom: 5px;
        left: 6px;
        font-size: 11px;
        font-weight: 600;
        color: var(--color-error, #c0392b);
        line-height: 1;
        pointer-events: none;
      }
      .arlo-opt-mono-input {
        flex: 1;
        padding: 10px 14px;
        font-size: 22px;
        font-family: 'Times New Roman', Times, serif;
        letter-spacing: 0.2em;
        text-indent: 0.2em; /* compensates for trailing letter-spacing so text stays visually centred */
        text-transform: uppercase;
        text-align: center;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        font-variant-emoji: text; /* force ♥ to render as black text, not red emoji */
        min-width: 0;
      }
      .arlo-opt-mono-input:focus { outline: 2px solid var(--color-foreground, #111); outline-offset: 2px; }
      .arlo-opt-mono-input.arlo-opt-error { border-color: var(--color-error, #c0392b); }
      .arlo-opt-mono-heart {
        padding: 0 14px;
        font-size: 22px; /* matches input font-size so ♥ is same height as letters */
        font-family: 'Times New Roman', Times, serif;
        font-variant-emoji: text;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        cursor: pointer;
        flex-shrink: 0;
        line-height: 1;
      }
      .arlo-opt-mono-heart:hover { border-color: var(--color-foreground, #111); }
      .arlo-opt-mono-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 6px;
      }
      .arlo-opt-mono-hint {
        font-size: 11px;
        color: var(--color-foreground-secondary, #666);
        white-space: nowrap;
      }
      .arlo-opt-mono-pips {
        display: flex;
        gap: 5px;
        flex-shrink: 0;
      }
      .arlo-opt-mono-pip {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--color-border, #d1d1d1);
        transition: background 0.15s;
        flex-shrink: 0;
      }
      .arlo-opt-mono-pip.filled {
        background: var(--color-foreground, #111);
      }

      /* ── Font Previewer ── */
      .arlo-opt-fp-input {
        width: 100%;
        padding: 16px;
        font-size: 36px;
        line-height: 1.4;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        box-sizing: border-box;
        transition: font-family 0.15s;
        outline: none;
        white-space: nowrap;
        overflow: hidden;
        cursor: text;
      }
      /* Placeholder for contenteditable — 30% lighter than normal text */
      .arlo-opt-fp-input[data-placeholder]:empty::before {
        content: attr(data-placeholder);
        color: currentColor;
        opacity: 0.15;
        pointer-events: none;
        /* Keep the placeholder in its authored case even when the input forces
           ALL CAPS (text-transform on the input would otherwise uppercase it). */
        text-transform: none;
      }
      .arlo-opt-fp-input:focus { box-shadow: 0 0 0 2px var(--color-foreground, #111); }
      .arlo-opt-fp-input.arlo-opt-error { border: 1px solid var(--color-error, #c0392b); box-shadow: none; }
      .arlo-opt-fp-fonts {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
      }
      .arlo-opt-fp-font-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        padding: 8px 12px;
        min-width: 72px;
        border: 1.5px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        cursor: pointer;
        border-radius: 4px;
        transition: border-color 0.15s;
        font-family: inherit;
      }
      .arlo-opt-fp-font-btn:hover { border-color: var(--color-foreground, #111); }
      .arlo-opt-fp-font-btn.active {
        border-color: var(--color-foreground, #111);
        border-width: 2px;
      }
      .arlo-opt-fp-font-sample { font-size: 26px; line-height: 1.2; }
      .arlo-opt-fp-font-name {
        font-size: 10px;
        color: var(--color-foreground-secondary, #666);
        text-align: center;
        max-width: 80px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: inherit;
      }

      /* ── Character counter ── */
      .arlo-opt-charcount {
        font-size: 12px;
        color: var(--color-foreground-secondary, #666);
        text-align: right;
        margin-top: 5px;
      }
      .arlo-opt-charcount.arlo-opt-charcount--warn {
        color: var(--color-error, #c0392b);
        font-weight: 600;
      }

      /* ── Error message ── */
      .arlo-opt-error-msg {
        font-size: 12px;
        color: var(--color-error, #c0392b);
        margin-top: 5px;
        display: none;
      }

      /* ── Hide Quantity label and Tax included note ── */
      .product-form__quantity { display: none !important; }
      .product__policies { display: none !important; }

      /* ── Quantity + Add to Cart on one row, always ── */
      .product-form__quantity-and-buy-buttons-wrapper {
        display: flex !important;
        flex-direction: row !important;
        align-items: flex-end !important;
        gap: 10px !important;
        flex-wrap: nowrap !important;
      }
      /* Quantity stepper widget (rendered via quantity-input snippet) */
      .product-form__quantity-and-buy-buttons-wrapper .quantity-input-wrapper--product-form {
        flex: 0 0 auto;
        width: auto !important;
        min-width: 0;
      }
      /* Hide the no-JS fallback quantity input — the stepper handles it */
      .product-form__quantity-and-buy-buttons-wrapper .product-form__quantity {
        display: none !important;
      }
      /* Hide the "Quantity" label above the stepper */
      .product-form__quantity-and-buy-buttons-wrapper .quantity-input-wrapper__labels {
        display: none !important;
      }
      /* ATC button group takes the rest of the row */
      .product-form__quantity-and-buy-buttons-wrapper .product-form__controls-group--submit {
        flex: 1 1 auto;
        min-width: 0;
      }
      /* Ensure the button itself fills its container */
      .product-form__quantity-and-buy-buttons-wrapper .product-form__cart-submit {
        width: 100% !important;
      }
    `;
    document.head.appendChild(s);
  }

  // ── Monogram text auto-shrink ─────────────────────────────────────────────────
  // Scales the input font-size down so text always fits within the box.
  // Uses a canvas to measure glyph widths + manual letter-spacing math.
  var _monoCanvas = null;
  function fitMonoInput(input) {
    var MAX_FS = 22;   // matches CSS default
    var MIN_FS = 9;
    var PADDING_H = 28; // 14px × 2 sides
    var LS_EM = 0.2;    // letter-spacing: 0.2em
    var TI_EM = 0.2;    // text-indent: 0.2em (visual compensation for trailing ls)

    var text = input.value || '';
    if (!text) { input.style.fontSize = ''; return; }

    var avail = (input.clientWidth || 200) - PADDING_H;
    if (avail <= 0) { input.style.fontSize = ''; return; }

    if (!_monoCanvas) _monoCanvas = document.createElement('canvas');
    var ctx = _monoCanvas.getContext('2d');

    // Binary search for largest font-size that fits
    var lo = MIN_FS, hi = MAX_FS, best = MIN_FS;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1;
      ctx.font = mid + 'px "Times New Roman", Times, serif';
      var w = ctx.measureText(text).width
            + text.length * LS_EM * mid   // letter-spacing per char
            + TI_EM * mid;                // text-indent offset
      if (w <= avail) { best = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }

    input.style.fontSize = best + 'px';
  }

  // ── Monogram pip counter ──────────────────────────────────────────────────────
  function updateMonoPips(pipsEl, filled, max) {
    pipsEl.innerHTML = '';
    for (var i = 0; i < max; i++) {
      var pip = document.createElement('span');
      pip.className = 'arlo-opt-mono-pip' + (i < filled ? ' filled' : '');
      pipsEl.appendChild(pip);
    }
  }

  // ── Label helper ──────────────────────────────────────────────────────────────
  function makeLabel(opt) {
    var lbl = document.createElement('label');
    lbl.className = 'arlo-opt-label';
    // Always add a step badge; the coordinator assigns the correct number after
    // all scripts have rendered (see _arloStepCoord above).
    var badge = document.createElement('span');
    badge.className = 'arlo-opt-step-badge';
    badge.textContent = '';
    lbl.appendChild(badge);
    if (window._arloStepCoord) window._arloStepCoord.register(badge);
    var text = document.createTextNode(opt.label);
    lbl.appendChild(text);
    if (opt.required) {
      var star = document.createElement('span');
      star.className = 'arlo-opt-required';
      star.textContent = ' *';
      lbl.appendChild(star);
    }
    return lbl;
  }

  // ── Build and inject fields ───────────────────────────────────────────────────
  function buildFields(options, form) {
    // Remove any existing container — but first rescue the uploader slot if it was
    // moved inside (arlo-options.js loads twice due to canvas-capture + uploadcare-fields
    // both including it; getElementById returns null for detached nodes, so we must
    // re-attach the slot to the document before the container is removed).
    var existing = document.getElementById(CONTAINER_ID);
    if (existing) {
      var orphanSlot = existing.querySelector('#arlo-uploader-slot');
      if (orphanSlot) existing.parentNode.insertBefore(orphanSlot, existing);
      existing.remove();
    }

    var container = document.createElement('div');
    container.id = CONTAINER_ID;

    options.forEach(function (opt) {
      var wrap = document.createElement('div');
      wrap.className = 'arlo-opt';
      wrap.dataset.optId = opt.id;

      var err = document.createElement('div');
      err.className = 'arlo-opt-error-msg';

      if (opt.type === 'select') {
        var sel = document.createElement('select');
        sel.className = 'arlo-opt-select';
        sel.name = 'properties[' + opt.label + ']';
        sel.dataset.required = opt.required ? 'true' : 'false';

        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = opt.placeholder || ('Select ' + opt.label + '…');
        sel.appendChild(placeholder);

        (opt.values || []).forEach(function (val) {
          var o = document.createElement('option');
          o.value = val;
          o.textContent = val;
          sel.appendChild(o);
        });

        err.textContent = 'Please select an option';
        sel.addEventListener('change', function () {
          sel.classList.remove('arlo-opt-error');
          err.style.display = 'none';
        });

        if (opt.label) wrap.appendChild(makeLabel(opt));
        wrap.appendChild(sel);
        wrap.appendChild(err);

      } else if (opt.type === 'buttons') {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + opt.label + ']';
        hidden.value = '';

        // Reuse the exact same grid + card classes as arlo-variant-buttons
        var btnsWrap = document.createElement('div');
        btnsWrap.className = 'arlo-opt-buttons arlo-vb-grid';
        // Single value → full width; multiple values → normal grid
        if ((opt.values || []).length === 1) {
          btnsWrap.style.gridTemplateColumns = '1fr';
        }

        err.textContent = 'Please select an option';

        // Shared bullets panel (same pattern as arlo-variant-buttons)
        var bulletsPanel = document.createElement('div');
        bulletsPanel.className = 'arlo-vb-bullets';
        var openBulletsLabel = null;

        // Use buttonValues (with description/bullets) if available, fall back to plain values
        var bvs = (opt.buttonValues && opt.buttonValues.length)
          ? opt.buttonValues
          : (opt.values || []).map(function (v) { return { label: v, description: '', bullets: [] }; });

        bvs.forEach(function (bv) {
          var val        = bv.label || '';
          var hasBullets = bv.bullets && bv.bullets.length;
          var hasImage   = bv.image && bv.image.trim();
          var hasPanel   = hasBullets || hasImage;

          var item = document.createElement('div');
          item.className = 'arlo-vb-item';

          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'arlo-opt-button arlo-vb-btn';
          btn._arloBv = bv; // sale mode repaints set badges from live prices
          if (hasPanel) btn.style.paddingRight = '26px';

          // Title
          var titleEl = document.createElement('div');
          titleEl.className = 'arlo-vb-title';
          titleEl.textContent = val;
          // Admin-configurable button title colour (option-level, applies to
          // every button in this option). Safe in the active state too — the
          // active styling only adds an outline + background tint, it never
          // inverts the text colour.
          if (opt.buttonTitleColor) titleEl.style.color = opt.buttonTitleColor;

          // Resolve the price to show. Button's own fields win; otherwise fall
          // back to the OPTION-level linked variant (the common case: one button
          // adding one linked product). A compare-at (real Shopify markdown)
          // renders "$was $now"; otherwise the usual "+ $now".
          var addonNow = (bv.addonPrice != null) ? parseFloat(bv.addonPrice)
                       : (opt.linkedVariantPrice != null ? opt.linkedVariantPrice / 100 : NaN);
          var addonCmp = (bv.addonCompareAt) ? parseFloat(bv.addonCompareAt)
                       : (opt.linkedVariantCompareAt ? opt.linkedVariantCompareAt / 100 : 0);
          var priceEl = null;
          if (!bv.hidePrice && addonNow && !bv.price && !bv.salePrice) {
            priceEl = document.createElement('div');
            priceEl.className = 'arlo-vb-price-row';
            if (addonCmp > addonNow) {
              var addonWas = document.createElement('span');
              addonWas.className = 'arlo-vb-compare';
              addonWas.textContent = '$' + addonCmp.toFixed(2);
              priceEl.appendChild(addonWas);
            }
            var addonSpan = document.createElement('span');
            addonSpan.className = 'arlo-vb-price';
            addonSpan.textContent = (addonCmp > addonNow ? '$' : '+ $') + addonNow.toFixed(2);
            priceEl.appendChild(addonSpan);
          } else if (!bv.hidePrice && (bv.price || bv.salePrice)) {
            priceEl = document.createElement('div');
            priceEl.className = 'arlo-vb-price-row';
            if (bv.compareAtPrice) {
              var compareSpan = document.createElement('span');
              compareSpan.className = 'arlo-vb-compare';
              compareSpan.textContent = '$' + parseFloat(bv.compareAtPrice).toFixed(2);
              priceEl.appendChild(compareSpan);
            }
            var bvPriceSpan = document.createElement('span');
            bvPriceSpan.className = 'arlo-vb-price';
            bvPriceSpan.textContent = bv.salePrice
              ? '$' + parseFloat(bv.salePrice).toFixed(2)
              : '$' + parseFloat(bv.price).toFixed(2);
            priceEl.appendChild(bvPriceSpan);
          }

          // Title and price share one line, centred together (title then price);
          // when there's no price the title renders on its own as before.
          if (priceEl) {
            var headerRow = document.createElement('div');
            headerRow.className = 'arlo-vb-header';
            headerRow.style.cssText = 'display:flex;align-items:baseline;justify-content:center;gap:8px;width:100%';
            priceEl.style.margin = '0';
            priceEl.style.whiteSpace = 'nowrap';
            headerRow.appendChild(titleEl);
            headerRow.appendChild(priceEl);
            btn.appendChild(headerRow);
          } else {
            btn.appendChild(titleEl);
          }

          // Description
          if (bv.description) {
            var descEl = document.createElement('div');
            descEl.className = 'arlo-vb-desc';
            descEl.textContent = bv.description;
            btn.appendChild(descEl);
          }

          // Discount badge — percentage as authored, or computed "Save $X" in dollar mode
          if (bv.discountBadge) {
            var bvBadge = document.createElement('span');
            bvBadge.className = 'arlo-vb-badge';
            if (bv.badgeStyle === 'dollar') _captureBasePrice();
            bvBadge.textContent = arloOptBadgeLabel(bv, _buttonBasePriceCents);
            btn.appendChild(bvBadge);
          } else if (opt.showLinkedBadge && addonCmp > addonNow) {
            // Auto discount tag from the linked variant's markdown (compare-at vs
            // price). "Save $X" or "Save Y%" per opt.linkedBadgeStyle.
            var autoBadge = document.createElement('span');
            autoBadge.className = 'arlo-vb-badge';
            var save = addonCmp - addonNow;
            autoBadge.textContent = (opt.linkedBadgeStyle === 'percent')
              ? 'Save ' + Math.round((save / addonCmp) * 100) + '%'
              : 'Save $' + save.toFixed(2);
            btn.appendChild(autoBadge);
          }

          btn.addEventListener('click', function () {
            if (!opt.required && btn.classList.contains('active')) {
              // Toggle off (optional buttons only)
              btn.classList.remove('active');
              hidden.value = '';
              restoreOptButtonPagePrice();
            } else {
              // Undo previously active button's price
              var prevActive = btnsWrap.querySelector('.arlo-opt-button.active');
              if (prevActive && prevActive !== btn) {
                restoreOptButtonPagePrice();
              }
              btnsWrap.querySelectorAll('.arlo-opt-button').forEach(function (b) { b.classList.remove('active'); });
              btn.classList.add('active');
              hidden.value = val;
              btnsWrap.classList.remove('arlo-opt-error');
              err.style.display = 'none';
              // Apply price — bv.addonPrice if set, else fall back to opt-level linkedVariantPrice
              var _bvForPrice = (bv.addonPrice || bv.price || bv.salePrice || bv.parentQty) ? bv
                : Object.assign({}, bv, { addonPrice: opt.linkedVariantPrice ? opt.linkedVariantPrice / 100 : null });
              setOptButtonPagePrice(_bvForPrice);
              // Switch product image if configured
              if (bv.productImageIndex) setProductImageByIndex(bv.productImageIndex);
              // Set parent product quantity if configured
              if (bv.parentQty) {
                var qtyInput = form.querySelector('input[name="quantity"]') ||
                               form.querySelector('.quantity__input') ||
                               form.querySelector('[data-quantity-input]');
                if (qtyInput) {
                  qtyInput.value = bv.parentQty;
                  qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
                  qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
              }
            }
          });
          btn._arloBv = bv;

          item.appendChild(btn);

          // "?" help badge for bullets and/or image
          if (hasPanel) {
            var helpBtn = document.createElement('button');
            helpBtn.type = 'button';
            helpBtn.className = 'arlo-vb-help';
            helpBtn.textContent = '?';
            helpBtn.setAttribute('aria-label', 'More info');
            helpBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              if (openBulletsLabel === val) {
                openBulletsLabel = null;
                bulletsPanel.classList.remove('open');
                helpBtn.classList.remove('open');
              } else {
                btnsWrap.querySelectorAll('.arlo-vb-help.open').forEach(function (h) { h.classList.remove('open'); });
                openBulletsLabel = val;
                bulletsPanel.innerHTML = '';
                // Image (if set)
                if (hasImage) {
                  var img = document.createElement('img');
                  img.src = bv.image.trim();
                  img.style.cssText = 'width:100%;border-radius:6px;display:block;margin-bottom:' + (hasBullets ? '8px' : '0');
                  bulletsPanel.appendChild(img);
                }
                // Bullets (if set)
                if (hasBullets) {
                  var ul = document.createElement('ul');
                  (bv.bullets || []).forEach(function (b) {
                    var li = document.createElement('li');
                    li.textContent = b;
                    ul.appendChild(li);
                  });
                  bulletsPanel.appendChild(ul);
                }
                bulletsPanel.classList.add('open');
                helpBtn.classList.add('open');
              }
            });
            item.appendChild(helpBtn);
          }

          btnsWrap.appendChild(item);
        });

        // Pre-select a button if defaultSelected is set — defaultIndex chooses which
        // one (0-based; defaults to the first). Use click() so the full handler fires
        // (price, qty, image switching).
        if (opt.defaultSelected && bvs.length) {
          var _allBtns = btnsWrap.querySelectorAll('.arlo-opt-button');
          var _defIdx = parseInt(opt.defaultIndex, 10) || 0;
          var _defBtn = _allBtns[_defIdx] || _allBtns[0];
          if (_defBtn) _defBtn.click();
        }

        if (opt.label) wrap.appendChild(makeLabel(opt));
        wrap.appendChild(btnsWrap);
        wrap.appendChild(bulletsPanel);
        wrap.appendChild(hidden);
        wrap.appendChild(err);

      } else if (opt.type === 'tiles') {
        var hiddenT = document.createElement('input');
        hiddenT.type = 'hidden';
        hiddenT.name = 'properties[' + opt.label + ']';
        hiddenT.value = '';

        var tilesWrap = document.createElement('div');
        tilesWrap.className = 'arlo-opt-tiles';

        err.textContent = 'Please select an option';

        (opt.tileValues || []).forEach(function (tv) {
          var tile = document.createElement('button');
          tile.type = 'button';
          tile.className = 'arlo-opt-tile';
          tile.title = tv.value || '';

          var swatch = document.createElement('div');
          swatch.className = 'arlo-opt-tile-swatch';
          if (tv.fillType === 'image' && tv.image) {
            swatch.style.backgroundImage = 'url(' + tv.image + ')';
          } else {
            swatch.style.backgroundColor = tv.color || '#cccccc';
          }

          var tileLabel = document.createElement('span');
          tileLabel.className = 'arlo-opt-tile-label';
          tileLabel.textContent = tv.value || '';

          tile.appendChild(swatch);
          tile.appendChild(tileLabel);

          tile.addEventListener('click', function () {
            if (!opt.required && tile.classList.contains('active')) {
              // Deselect on second click (non-required only)
              tile.classList.remove('active');
              hiddenT.value = '';
            } else {
              tilesWrap.querySelectorAll('.arlo-opt-tile').forEach(function (t) { t.classList.remove('active'); });
              tile.classList.add('active');
              hiddenT.value = tv.value || '';
              tilesWrap.classList.remove('arlo-opt-error');
              err.style.display = 'none';
              // Switch product image if this tile is linked to one
              // console.log('[ArloOptions] tile clicked, linkedImageIndex=', tv.linkedImageIndex, 'linkedImageSrc=', tv.linkedImageSrc);
              if (tv.linkedImageIndex != null) {
                arloSwitchProductImage(tv.linkedImageIndex);
              } else if (tv.linkedImageSrc) {
                var srcBase = tv.linkedImageSrc.split('?')[0].split('/').pop().replace(/_\d+x[\d]*\.(jpg|jpeg|png|gif|webp)/i, '.$1').replace(/\.\w+$/, '');
                // console.log('[ArloOptions] looking for srcBase:', srcBase);
                // Search ONLY inside the product photo container to avoid uploadcare slots
                var photoContainer = document.querySelector(
                  '.product__media-container.above-mobile, .product__media-container.below-mobile'
                );
                var wrappers = photoContainer
                  ? photoContainer.querySelectorAll('[data-product-media-wrapper]')
                  : document.querySelectorAll('[data-product-media-wrapper]');
                // console.log('[ArloOptions] wrappers in photo container:', wrappers.length);
                var foundIdx = -1;
                wrappers.forEach(function (w, wi) {
                  if (foundIdx !== -1) return;
                  var img = w.querySelector('img');
                  if (!img) return;
                  // Check data-src (lazy-loaded) first, then fall back to src
                  var rawSrc = img.getAttribute('data-src') || img.getAttribute('data-srcset') || img.src || '';
                  // data-srcset may have multiple entries — grab the first URL
                  rawSrc = rawSrc.split(',')[0].trim().split(' ')[0];
                  var imgBase = rawSrc.split('?')[0].split('/').pop().replace(/_\d+x[\d]*\.(jpg|jpeg|png|gif|webp)/i, '.$1').replace(/\.\w+$/, '');
                  // console.log('[ArloOptions] wrapper', wi, 'imgBase:', imgBase);
                  if (imgBase === srcBase) foundIdx = wi;
                });
                // console.log('[ArloOptions] foundIdx:', foundIdx);
                if (foundIdx !== -1) arloSwitchProductImage(foundIdx);
              } else {
                // console.log('[ArloOptions] no linked image data on this tile');
              }
            }
          });

          tilesWrap.appendChild(tile);
        });

        if (opt.label) wrap.appendChild(makeLabel(opt));
        wrap.appendChild(tilesWrap);
        wrap.appendChild(hiddenT);
        wrap.appendChild(err);

      } else if (opt.type === 'monogram') {
        var maxLen = opt.maxChars || 7;
        // Allowed: A-Z, 0-9, ♥, &, space
        var MONO_ALLOWED = /^[A-Za-z0-9♥& ]$/;

        var monoHidden = document.createElement('input');
        monoHidden.type = 'hidden';
        monoHidden.name = 'properties[' + (opt.label || 'Monogram') + ']';
        monoHidden.value = '';

        var monoInput = document.createElement('input');
        monoInput.type = 'text';
        monoInput.className = 'arlo-opt-mono-input';
        monoInput.autocomplete = 'off';
        monoInput.autocapitalize = 'characters';
        monoInput.autocorrect = 'off';
        monoInput.spellcheck = false;
        monoInput.placeholder = '_ '.repeat(maxLen).trim();

        var pipsEl = document.createElement('div');
        pipsEl.className = 'arlo-opt-mono-pips';
        updateMonoPips(pipsEl, 0, maxLen);

        monoInput.addEventListener('input', function () {
          var raw = monoInput.value.toUpperCase();
          var filtered = '';
          var heartSeen = false;
          for (var ci = 0; ci < raw.length && filtered.length < maxLen; ci++) {
            var ch = raw[ci];
            if (ch === '♥') {
              if (heartSeen) continue; // only one heart allowed
              heartSeen = true;
            }
            if (MONO_ALLOWED.test(ch)) filtered += ch;
          }
          if (monoInput.value !== filtered) {
            var pos = monoInput.selectionStart - (monoInput.value.length - filtered.length);
            monoInput.value = filtered;
            monoInput.setSelectionRange(Math.max(0, pos), Math.max(0, pos));
          }
          monoHidden.value = filtered;
          updateMonoPips(pipsEl, filtered.length, maxLen);
          fitMonoInput(monoInput);
          monoInput.classList.remove('arlo-opt-error');
          err.style.display = 'none';
        });

        var heartBtn = document.createElement('button');
        heartBtn.type = 'button';
        heartBtn.className = 'arlo-opt-mono-heart';
        heartBtn.textContent = '♥︎'; // U+FE0E = text variation selector → forces black ♥, not red emoji
        heartBtn.title = 'Insert heart';
        heartBtn.addEventListener('click', function () {
          var pos = monoInput.selectionStart || monoInput.value.length;
          var current = monoInput.value;
          if (current.length >= maxLen) return;
          if (current.indexOf('♥') !== -1) return; // only one heart allowed
          var newVal = current.slice(0, pos) + '♥' + current.slice(pos);
          monoInput.value = newVal.slice(0, maxLen);
          monoHidden.value = monoInput.value;
          updateMonoPips(pipsEl, monoInput.value.length, maxLen);
          fitMonoInput(monoInput);
          monoInput.focus();
          monoInput.setSelectionRange(pos + 1, pos + 1);
        });

        var ampBtn = document.createElement('button');
        ampBtn.type = 'button';
        ampBtn.className = 'arlo-opt-mono-heart';
        ampBtn.textContent = '&';
        ampBtn.title = 'Insert &';
        ampBtn.addEventListener('click', function () {
          var pos = monoInput.selectionStart || monoInput.value.length;
          var current = monoInput.value;
          if (current.length >= maxLen) return;
          var newVal = current.slice(0, pos) + '&' + current.slice(pos);
          monoInput.value = newVal.slice(0, maxLen);
          monoHidden.value = monoInput.value;
          updateMonoPips(pipsEl, monoInput.value.length, maxLen);
          fitMonoInput(monoInput);
          monoInput.focus();
          monoInput.setSelectionRange(pos + 1, pos + 1);
        });

        var monoRow = document.createElement('div');
        monoRow.className = 'arlo-opt-mono-row';
        monoRow.appendChild(monoInput);
        monoRow.appendChild(heartBtn);
        monoRow.appendChild(ampBtn);
        // Required star sits at bottom-left of the input box, doesn't affect label line height
        if (opt.required) {
          var monoStar = document.createElement('span');
          monoStar.className = 'arlo-opt-mono-required';
          monoStar.textContent = '*';
          monoRow.appendChild(monoStar);
        }

        var hint = document.createElement('span');
        hint.className = 'arlo-opt-mono-hint';
        hint.textContent = 'A–Z · 0–9 · ♥︎ · & · space · up to ' + maxLen + ' characters'; // ︎ forces text (black) heart

        var footer = document.createElement('div');
        footer.className = 'arlo-opt-mono-footer';
        footer.appendChild(hint);
        footer.appendChild(pipsEl);

        err.textContent = 'Please enter your monogram';

        // Label without the * — star is shown at bottom-left of the input box instead
        // Always show step badge; fall back to 'Monogram' if no label set
        wrap.appendChild(makeLabel(Object.assign({}, opt, { required: false, label: opt.label || 'Monogram' })));
        wrap.appendChild(monoRow);
        wrap.appendChild(footer);
        wrap.appendChild(monoHidden);
        wrap.appendChild(err);

      } else if (opt.type === 'font-previewer') {
        // Font previewer — two cases:
        // Case 1 (multiple fonts): customer types text + picks font →
        //   properties[label] = text, properties[fontPropertyLabel] = font display name
        // Case 2 (single fixed font): styled text input previewing what they'll receive →
        //   properties[label] = text only

        var fpLabel = opt.textPropertyLabel || opt.label || 'Personalisation';
        var fpPlaceholder = opt.placeholder || 'Type here to preview…';
        var fpFontPropLabel = opt.fontPropertyLabel || 'Font style & size';

        // Resolve font ids against the global font registry
        var allFonts = (window.ArloProductConfig && window.ArloProductConfig.fonts) || [];
        var fpFontIds = opt.fonts || [];
        var resolvedFonts = fpFontIds.map(function (fid) {
          return allFonts.find(function (f) { return f.id === fid; }) ||
                 { id: fid, name: fid, cssClass: fid, displayName: '' };
        });

        var fpHidden = document.createElement('input');
        fpHidden.type = 'hidden';
        fpHidden.name = 'properties[' + fpLabel + ']';
        fpHidden.value = '';
        fpHidden.dataset.required = opt.required ? 'true' : 'false';

        var fpFontHidden = null;

        // contenteditable div — avoids browser clipping of script font ascenders/descenders
        var fpInput = document.createElement('div');
        fpInput.contentEditable = 'true';
        fpInput.className = 'arlo-opt-fp-input';
        fpInput.dataset.placeholder = fpPlaceholder;
        fpInput.spellcheck = false;
        var fpWrap = null; // set in canvas-png branch; wrapper div used there instead
        var _circleCenter = null; // set for round canvas-png boxes (re-centres the text block)
        var _fpTrailingBlankPx = function () { return 0; }; // canvas boxes: blank lines centring ignores
        var _fpCentre = function () {};                      // canvas boxes: re-centre (circle or rectangle)
        var _circleInside = null; // set for round canvas-png boxes (true if all text is inside the circle)

        // ── Apply input box styling ──────────────────────────────────────────
        if (opt.outputFormat === 'canvas-png' && opt.canvasSettings) {
          // Canvas PNG mode: the input box mirrors the artboard so the customer
          // sees exactly what the final product will look like.
          var cs = opt.canvasSettings;
          var wMm   = cs.templateWidthMm  || 150;
          var hMm   = cs.templateHeightMm || 50;
          var fsPt  = cs.fontSize  || 60;
          var dpi   = cs.dpi       || 300;
          var hAlign = cs.hAlign  || 'center';
          var vAlign = cs.vAlign  || 'middle';
          var vOff   = cs.vOffset || 0;
          var kerning = cs.kerning || 0;
          var leadingMult = cs.leading || 1.4;

          // 1. Wrapper is a fixed-size artboard frame (aspect-ratio box).
          //    fpInput is absolutely positioned inside it, so it can never
          //    overflow the frame regardless of how much text is typed.
          var fpWrap = document.createElement('div');
          var isCircleBox = cs.shape === 'circle';
          fpWrap.style.aspectRatio  = wMm + ' / ' + hMm;
          fpWrap.style.height       = 'auto';
          fpWrap.style.width        = '100%';
          fpWrap.style.position     = 'relative';
          fpWrap.style.background   = 'var(--color-background, #f8f8f8)';
          fpWrap.style.border       = '1px solid rgba(var(--color-foreground, 18,18,18), 0.2)';
          fpWrap.style.borderRadius = isCircleBox ? '50%' : '4px';
          fpWrap.style.overflow     = 'hidden';
          fpWrap.style.cursor       = 'text';
          fpWrap.style.marginLeft   = 'auto';
          fpWrap.style.marginRight  = 'auto';

          // Optional width cap — percentage of the container (100 = full width, 60 = 60%).
          // Height auto-follows from the aspect-ratio, so the box stays proportional.
          var displayW = cs.displayWidthPct || 100;
          if (displayW < 100) {
            fpWrap.style.maxWidth = displayW + '%';
          }

          // Clicking anywhere in the wrapper focuses the inner input
          fpWrap.addEventListener('click', function () { fpInput.focus(); });
          // Forward focus ring to wrapper
          fpInput.addEventListener('focus', function () { fpWrap.style.boxShadow = '0 0 0 2px var(--color-foreground, #111)'; });
          fpInput.addEventListener('blur',  function () { fpWrap.style.boxShadow = ''; });

          // 2. A non-interactive flex container sits between fpWrap and fpInput.
          //    It handles vertical alignment without putting display:flex on the
          //    contenteditable itself (which would misplace the cursor).
          var fpFlex = document.createElement('div');
          fpFlex.style.position        = 'absolute';
          fpFlex.style.top             = '0';
          fpFlex.style.bottom          = '0';
          fpFlex.style.left            = '0';
          fpFlex.style.right           = '0';
          if (isCircleBox) {
            // Circle (2026-09-28): plain block, NOT flex. Two floated "walls" with
            // a curved shape-outside (added below, before fpInput) make the browser
            // wrap every line to the circle's width at that height, so no line can
            // run past the curve. They only shorten line boxes if fpInput shares
            // this block formatting context — a flex item would be its own BFC and
            // ignore them. Vertical centring is done with padding-top instead
            // (_circleCenter).
            fpFlex.style.display       = 'block';
          } else {
            fpFlex.style.display         = 'flex';
            fpFlex.style.flexDirection   = 'column';
            fpFlex.style.justifyContent  = vAlign === 'top'    ? 'flex-start'
                                         : vAlign === 'bottom' ? 'flex-end'
                                         : 'center';
          }
          fpFlex.style.overflow        = 'hidden';
          fpFlex.style.pointerEvents   = 'none'; // clicks fall through to fpWrap handler

          // 3. fpInput is a normal block inside fpFlex — no flex on it, cursor works correctly.
          fpInput.style.width         = '100%';
          fpInput.style.maxHeight     = '100%'; // can never overflow fpFlex
          fpInput.style.boxSizing     = 'border-box';
          fpInput.style.pointerEvents = 'auto';
          fpInput.style.background    = 'transparent';
          fpInput.style.border        = 'none';
          fpInput.style.boxShadow     = 'none';
          fpInput.style.whiteSpace    = 'pre-wrap';
          fpInput.style.wordBreak     = 'normal';
          fpInput.style.overflowWrap  = 'break-word';
          fpInput.style.hyphens       = 'none';
          fpInput.style.overflow      = 'hidden';
          if (isCircleBox) {
            // overflow:hidden would make fpInput its own block formatting context,
            // and then the curved float walls wouldn't shorten its lines. fpFlex
            // (overflow:hidden) + the round fpWrap still do the clipping.
            fpInput.style.overflow  = 'visible';
            fpInput.style.maxHeight = 'none';
            fpInput.style.display   = 'block';
            // No mid-word splitting: with break-word the browser will cram single
            // letters into the thin slivers at the top/bottom of the curve. With
            // 'normal' a word that doesn't fit moves down to where it does.
            fpInput.style.overflowWrap = 'normal';
          }
          // The base .arlo-opt-fp-input CSS class hardcodes line-height:1.4 — override
          // with the admin's configured Line Spacing value so it actually takes effect.
          fpInput.style.lineHeight    = leadingMult + '';

          // 3. Text formatting from canvas settings
          fpInput.style.textAlign      = hAlign;
          if (cs.color)  fpInput.style.color         = cs.color;
          // Letter Spacing % (admin) → em, so it scales with the box exactly like
          // the PNG does. Older options only have the px "kerning".
          if (cs.letterSpacingPct) fpInput.style.letterSpacing = (cs.letterSpacingPct / 100) + 'em';
          else if (kerning)        fpInput.style.letterSpacing = kerning + 'px';

          // 4. Scale font size proportionally to rendered box height
          //    pt → fraction of artboard height: fsPt / (hMm/25.4*72)
          var artboardHPt    = hMm / 25.4 * 72;
          var artboardFullPx = (hMm / 25.4) * dpi;
          var fontFraction   = fsPt / artboardHPt;

          function _applyCanvasBoxFont() {
            var w = fpWrap.offsetWidth;
            if (!w) return;
            // Set an explicit pixel height so the box never grows with content.
            // aspect-ratio alone doesn't hard-lock height; content can push it open.
            var h = Math.round(w * hMm / wMm);
            fpWrap.style.height = h + 'px';
            var fsPx = Math.round(h * fontFraction);
            fpInput.style.fontSize = fsPx + 'px';

            // Vertical alignment is handled by fpFlex (justify-content).
            // Padding here is for side insets + vOffset nudge only.
            // IMPORTANT: must scale with fsPx (not a fixed px value) so the amount
            // of text that fits stays identical regardless of the box's on-screen
            // width (Display Width % / container size / screen rotation). A fixed
            // px pad eats a bigger share of a smaller box, shrinking usable text
            // area disproportionately and changing line-fit at different widths.
            // 0.5 matches edgePad's factor in renderTextToCanvas() (the actual PNG
            // export), so the live box's capacity also mirrors the real print output.
            if (isCircleBox) {
              // Circle: the curved float walls are the side margin (incl. the 5%
              // safe zone), so no side/bottom padding; top padding centres the block.
              fpInput.style.paddingLeft   = '0px';
              fpInput.style.paddingRight  = '0px';
              fpInput.style.paddingBottom = '0px';
              _circleCenter();
              return;
            }
            var sidePad = Math.round(fsPx * 0.5);
            var vOffDisplay = vOff ? Math.round(vOff * h / artboardFullPx) : 0;
            fpInput.style.paddingTop    = Math.max(0, sidePad + vOffDisplay) + 'px';
            fpInput.style.paddingBottom = Math.max(0, sidePad - vOffDisplay) + 'px';
            fpInput.style.paddingLeft   = sidePad + 'px';
            fpInput.style.paddingRight  = sidePad + 'px';
          }

          // Height (px) of empty lines at the END of the box that centring should
          // ignore (2026-10-08): everything below the last line with text — but
          // while the customer is typing, the caret's own line is kept (Enter at
          // the end needs a visible line to type on). So pasted / extra blank
          // lines never push the message off-centre, live, before the PNG.
          _fpTrailingBlankPx = function () {
            var st = getComputedStyle(fpInput);
            var ib = fpInput.getBoundingClientRect();
            var contentBottom = ib.top + fpInput.scrollHeight - (parseFloat(st.paddingBottom) || 0);
            var tw = document.createTreeWalker(fpInput, NodeFilter.SHOW_TEXT, null, false);
            var n, last = null, idx = -1;
            while ((n = tw.nextNode())) {
              var m = n.nodeValue.search(/\S\s*$/);
              if (m !== -1) { last = n; idx = m; }
            }
            if (!last) return 0;
            var r = document.createRange();
            r.setStart(last, idx); r.setEnd(last, idx + 1);
            var rs = r.getClientRects(), rc = rs[rs.length - 1];
            if (!rc) return 0;
            var lh = parseFloat(st.lineHeight) || rc.height;
            var keep = rc.bottom + Math.max(0, (lh - rc.height) / 2);   // bottom of the last text line
            var sel = window.getSelection && window.getSelection();
            if (document.activeElement === fpInput && sel && sel.rangeCount && fpInput.contains(sel.focusNode)) {
              var cr = sel.getRangeAt(0).getClientRects()[0];
              if (cr && cr.height) keep = Math.max(keep, cr.bottom + Math.max(0, (lh - cr.height) / 2));
              else if (sel.focusNode.nodeType === 1 && sel.focusNode !== fpInput) keep = Math.max(keep, sel.focusNode.getBoundingClientRect().bottom);
              else keep = contentBottom; // can't tell where the caret is — ignore nothing
            }
            return Math.max(0, contentBottom - keep);
          };
          // Rectangles: the flex box centres fpInput as a whole, so pull the
          // ignored blank lines out of the centring with a negative bottom margin.
          _fpCentre = function () {
            if (_circleCenter) { _circleCenter(); return; }
            if (vAlign === 'top') return;
            fpInput.style.marginBottom = -_fpTrailingBlankPx() + 'px';
          };

          if (isCircleBox) {
            // Vertically place the text block inside the circle with padding-top.
            // Line widths depend on where each line sits (the walls are curved), and
            // the block height depends on the line widths, so settle it in a few
            // passes. Only padding-top changes — the browser still does all wrapping.
            _circleCenter = function () {
              var H = fpFlex.clientHeight;
              if (!H) return;
              var pad = parseFloat(fpInput.style.paddingTop) || 0;
              for (var k = 0; k < 5; k++) {
                fpInput.style.paddingTop = pad + 'px';
                var contentH = fpInput.scrollHeight - pad - _fpTrailingBlankPx();
                var next = vAlign === 'top'    ? 0
                         : vAlign === 'bottom' ? (H - contentH)
                         : (H - contentH) / 2;
                next = Math.max(0, next);
                if (Math.abs(next - pad) < 0.5) { pad = next; break; }
                pad = next;
              }
              fpInput.style.paddingTop = pad + 'px';
            };

            // Hard rule for round boxes: every letter on screen must sit inside
            // the safe circle. Measured from what's actually rendered, so it holds
            // whatever the browser does with the curve. The overflow guard (typing
            // + paste) rejects any edit that fails this, same as running out of
            // height. Small tolerance (2% of the radius) for glyph-box rounding.
            _circleInside = function () {
              var br = fpFlex.getBoundingClientRect();
              if (!br.width || !br.height) return true;
              var cx = br.left + br.width / 2, cy = br.top + br.height / 2;
              var R = Math.min(br.width, br.height) / 2;
              var lim = R * (1 - 2 * CIRCLE_SAFE_ZONE) + R * 0.02;
              var lim2 = lim * lim;
              var rg = document.createRange();
              var tw = document.createTreeWalker(fpInput, NodeFilter.SHOW_TEXT, null, false);
              var tn;
              while ((tn = tw.nextNode())) {
                var v = tn.nodeValue;
                for (var i = 0; i < v.length; i++) {
                  if (/\s/.test(v.charAt(i))) continue;
                  rg.setStart(tn, i); rg.setEnd(tn, i + 1);
                  var rs = rg.getClientRects();
                  for (var q = 0; q < rs.length; q++) {
                    var r = rs[q];
                    if (!r.height) continue;
                    var dx = Math.max(Math.abs(r.left - cx), Math.abs(r.right - cx));
                    var dy = Math.max(Math.abs(r.top - cy), Math.abs(r.bottom - cy));
                    if (dx * dx + dy * dy > lim2) return false;
                  }
                }
              }
              return true;
            };
          }

          if (window.ResizeObserver) {
            var _ro = new ResizeObserver(_applyCanvasBoxFont);
            _ro.observe(fpWrap);
          } else {
            setTimeout(_applyCanvasBoxFont, 60);
          }

          if (isCircleBox) {
            // Curved walls: the left float excludes the left half of the box that
            // lies outside the (safe-zone) circle, the right float the right half.
            // shape-outside works per line box, so every line — including its
            // leading — stays inside the circle. Fonts changing/loading moves lines,
            // so re-centre then too.
            fpFlex.appendChild(_makeCircleWall('left',  CIRCLE_SAFE_ZONE));
            fpFlex.appendChild(_makeCircleWall('right', CIRCLE_SAFE_ZONE));
            fpWrap.classList.add('arlo-opt-fp-artboard--circle');
            if (document.fonts) {
              if (document.fonts.ready) document.fonts.ready.then(function () { _circleCenter(); });
              if (document.fonts.addEventListener) {
                document.fonts.addEventListener('loadingdone', function () { _circleCenter(); });
              }
            }
          }
          fpWrap.classList.add('arlo-opt-fp-artboard');
          fpFlex.appendChild(fpInput);
          fpWrap.appendChild(fpFlex);

        } else {
          // Text mode — use the manually configured inputStyle settings
          var is = opt.inputStyle || {};
          if (is.fontSize)  fpInput.style.fontSize  = is.fontSize + 'px';
          if (is.textAlign) fpInput.style.textAlign = is.textAlign;
          if (is.padding)   fpInput.style.padding   = is.padding;
          if (is.height)    fpInput.style.height    = is.height + 'px';

          // Vertical offset — shift text up/down by adjusting padding
          var vOffset = opt.verticalOffset || 0;
          if (vOffset) {
            var basePad = is.padding ? parseInt(is.padding) : 16;
            fpInput.style.paddingTop    = Math.max(0, basePad + vOffset) + 'px';
            fpInput.style.paddingBottom = Math.max(0, basePad - vOffset) + 'px';
          }
        }

        // Apply initial font directly to the input
        var activeCss = resolvedFonts.length ? resolvedFonts[0].id : '';
        if (activeCss) fpInput.style.fontFamily = "'" + activeCss + "'";

        // All-caps mode — display via CSS (keeps the caret smooth while typing);
        // the actual submitted value + canvas text are uppercased at read time.
        var fpUpper = !!opt.uppercase;
        if (fpUpper) fpInput.style.textTransform = 'uppercase';

        // Canvas-png mode allows Enter (multi-line like InDesign text box).
        // Text mode keeps single-line behaviour.
        var fpIsCanvas = opt.outputFormat === 'canvas-png' && opt.canvasSettings;

        // Small tolerance for the artboard-overflow guard below. Browsers' actual
        // rendered line-box height is often a px or two taller than the CSS
        // line-height implies (font metrics/ascent-descent), so on tight artboards
        // (e.g. a 2-line message that mathematically just fits) scrollHeight can
        // tick past clientHeight before the line is even visible — blocking input
        // while there's still visible room in the box. This buffer avoids that
        // premature/hair's-breadth block without disabling the real overflow guard.
        var CANVAS_OVERFLOW_TOLERANCE = 3;

        // Last content state that FIT inside the artboard (innerHTML preserves
        // the line structure exactly). The overflow guard is now try-then-
        // revert: let the edit happen, measure the REAL resulting height, and
        // restore this snapshot only if it genuinely overflows. The old
        // approach predicted overflow before the edit (Enter = +1 line-height)
        // and blocked — wrongly, because an Enter inserted mid-text usually
        // converts a soft wrap into a hard break WITHOUT adding a line. That
        // made re-formatting existing text impossible ("you need to get the
        // formatting right the first time").
        var _fpLastGood = null;
        // Reentrancy guard: true while the paste trim-to-fit loop below is
        // programmatically inserting trial text (execCommand fires 'input'
        // synchronously — without this the input handler would fight the loop).
        var _fpAdjusting = false;
        function _fpSnapshot() { _fpLastGood = fpInput.innerHTML; }
        function _fpRevert() {
          if (_fpLastGood === null) return;
          fpInput.innerHTML = _fpLastGood;
          try {
            var r = document.createRange();
            r.selectNodeContents(fpInput);
            r.collapse(false);
            var s = window.getSelection();
            s.removeAllRanges();
            s.addRange(r);
          } catch (e) {}
        }
        function _fpText() {
          // innerText preserves line breaks (textContent does NOT — divs
          // concatenate); strip the trailing newline block layout adds.
          var t = fpInput.innerText !== undefined ? fpInput.innerText : fpInput.textContent;
          return t.replace(/\n+$/, '');
        }
        // 2026-10-08: blank lines AFTER the message are always removed. Customers
        // who write in Notes and paste (or press Enter a few extra times) left
        // empty lines at the end; the box centres the whole block INCLUDING
        // those lines, so the message sat high on screen and in the PNG (round
        // boxes copy the screen exactly). Removed on paste (below), when the
        // customer leaves the box, and right before the PNG is made
        // (fpInput._arloTidyEnd, called by the add-to-cart task builder).
        // Blank lines BETWEEN lines of text are kept.
        function _trimTail(el) {
          var changed = false;
          while (el.lastChild) {
            var n = el.lastChild;
            if (n.nodeType === 3) {
              var v = n.nodeValue;
              if (v === '' || (/[\r\n]/.test(v) && !v.replace(/\s/g, '').length)) {
                el.removeChild(n); changed = true; continue;        // empty / newline-only text
              }
              var cut = v.replace(/[ \t\u00a0]*(\r?\n[\s]*)+$/, '');  // text then trailing newlines
              if (cut !== v) { n.nodeValue = cut; changed = true; }
              break;
            }
            if (n.nodeName === 'BR') { el.removeChild(n); changed = true; continue; }
            if (n.nodeType === 1) {
              if (!n.textContent.replace(/\s/g, '').length) { el.removeChild(n); changed = true; continue; } // empty line block
              if (_trimTail(n)) changed = true;                       // last line: trim inside it
              break;
            }
            el.removeChild(n); changed = true;                         // comments etc.
          }
          return changed;
        }
        // Height the text block takes up, for the "does it fit" checks. For
        // round boxes padding-top is only centring, so it's left out — the
        // whole block (text + any blank lines) must fit, exactly as before.
        function _fpUsedH() {
          return fpInput.scrollHeight - (_circleCenter ? (parseFloat(fpInput.style.paddingTop) || 0) : 0);
        }
        function _fpTidyEnd() {
          if (!_trimTail(fpInput)) { _fpCentre(); return; }
          _fpCentre();
          var t = _fpText();
          fpHidden.value = fpUpper ? t.toUpperCase() : t;
          _fpSnapshot();
        }
        if (fpIsCanvas) {
          fpInput._arloTidyEnd = _fpTidyEnd;
          fpInput.addEventListener('blur', _fpTidyEnd);
          // Caret moved (tap / arrow keys) → which blank lines count may change.
          document.addEventListener('selectionchange', function () {
            if (document.activeElement === fpInput) _fpCentre();
          });
        }

        // 2026-08-18: take the initial (empty) snapshot IMMEDIATELY. _fpRevert()
        // no-ops while _fpLastGood is null, and snapshots were only ever taken
        // after a fitting edit — so if a customer's FIRST action was pasting a
        // too-long message into the empty box, the overflow guard fired but the
        // revert silently did nothing, and the full message went through to the
        // PNG. Typing first worked (every keystroke snapshots), which is why
        // this only ever showed up with paste.
        _fpSnapshot();

        // Honour maxChars in all modes (canvas-png and text).
        // In canvas-png the artboard clips overflow visually, but an explicit
        // maxChars limit from the admin is always enforced.
        var fpMaxChars = opt.maxChars ? opt.maxChars : null;

        var fpCounter = null;
        if (fpMaxChars) {
          fpCounter = document.createElement('div');
          fpCounter.className = 'arlo-opt-charcount';
          fpCounter.textContent = '0 / ' + fpMaxChars;
        }
        fpInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' && !fpIsCanvas) { e.preventDefault(); return; }
          // Hard-stop at maxChars — block printable keys when limit is reached,
          // but allow navigation, deletion, cut, copy, select-all, etc.
          if (fpMaxChars && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            var cur = fpInput.textContent.replace(/[\r\n]/g, '');
            var sel = window.getSelection ? window.getSelection().toString() : '';
            if (cur.length >= fpMaxChars && !sel) {
              e.preventDefault();
              return;
            }
          }
          // 2026-08-07: the old pre-emptive overflow block lived here (predict
          // "Enter = +1 line-height" and preventDefault). Removed — the
          // prediction was wrong for the most common re-formatting edit
          // (an Enter that converts a soft wrap into a hard break adds NO
          // height) and made it impossible to reformat already-typed text.
          // Overflow is now handled AFTER the edit in the input handler:
          // measure the real resulting height, revert to the last fitting
          // snapshot if it genuinely overflows (see _fpSnapshot/_fpRevert).
          // Canvas-png: prevent the browser from auto-scrolling the page to follow
          // the caret when it moves below the clipped artboard overflow area.
          if (fpIsCanvas) {
            var _sy = window.scrollY;
            requestAnimationFrame(function () {
              if (window.scrollY !== _sy) window.scrollTo(window.scrollX, _sy);
            });
          }
        });

        // Strip HTML on paste; enforce maxChars and artboard overflow
        fpInput.addEventListener('paste', function (e) {
          e.preventDefault();
          var text = (e.clipboardData || window.clipboardData).getData('text/plain');
          if (!fpIsCanvas) text = text.replace(/[\r\n]/g, '');
          // Canvas: drop blank lines at the end of the pasted text (see _fpTidyEnd).
          else text = text.replace(/[ \t\u00a0]*(\r?\n[ \t\u00a0]*)+$/, '');
          if (fpMaxChars) {
            var cur = fpInput.textContent.replace(/[\r\n]/g, '');
            var sel = window.getSelection ? window.getSelection().toString() : '';
            var used = cur.length - sel.length;
            var remaining = Math.max(0, fpMaxChars - used);
            text = text.slice(0, remaining);
          }
          // Canvas: snapshot the pre-paste state so the overflow handling below
          // has an exact, guaranteed-fitting restore point (current content
          // always fits, by invariant — every fitting edit re-snapshots).
          // _fpAdjusting suppresses the input handler for the initial insert:
          // execCommand fires 'input' SYNCHRONOUSLY, and the input handler's
          // own overflow check would otherwise revert the whole paste before
          // the trim-to-fit below ever gets a chance to run. The rAF callback
          // takes over the input handler's duties (sanitize, hidden field,
          // counter) for this edit.
          if (fpIsCanvas) { _fpSnapshot(); _fpAdjusting = true; }
          document.execCommand('insertText', false, text);
          // Canvas-png: if the paste overflows the artboard, trim it to the
          // longest prefix that still fits (2026-08-18 — previously the WHOLE
          // paste was reverted, and before the initial-snapshot fix above the
          // revert could silently no-op, letting the full message through).
          // Trimming matches the typing behaviour: you can fill the box, then
          // it stops. Binary search on the pasted text's length; each trial
          // restores the pre-paste snapshot then re-inserts a prefix via
          // execCommand so the line structure is built by the browser exactly
          // as a real paste would. (Known trade-off: if the paste happened
          // mid-message, the trimmed text lands at the end instead — rare, and
          // far better than the message silently overflowing the artwork.)
          if (fpIsCanvas) {
            requestAnimationFrame(function () {
              sanitizeEditableInPlace(fpInput);
              var capacity = fpFlex.clientHeight || fpInput.clientHeight;
              var fits = function () {
                if (_circleCenter) _circleCenter(); // circle: re-centre before measuring
                return _fpUsedH() <= capacity + CANVAS_OVERFLOW_TOLERANCE &&
                       (!_circleInside || _circleInside());
              };
              var trimmed = false, best = 0;
              if (!fits()) {
                trimmed = true;
                var lo = 0, hi = text.length - 1;
                while (lo <= hi) {
                  var mid = Math.floor((lo + hi) / 2);
                  _fpRevert(); // restore pre-paste content, caret at end
                  if (mid > 0) document.execCommand('insertText', false, text.slice(0, mid));
                  if (fits()) { best = mid; lo = mid + 1; }
                  else { hi = mid - 1; }
                }
                _fpRevert();
                if (best > 0) document.execCommand('insertText', false, text.slice(0, best));
                sanitizeEditableInPlace(fpInput);
                if (_circleCenter) _circleCenter();
              }
              _fpAdjusting = false;
              _fpCentre();
              _fpSnapshot();
              var t = _fpText();
              fpHidden.value = fpUpper ? t.toUpperCase() : t;
              fpInput.classList.remove('arlo-opt-error');
              if (fpCounter) {
                var len = t.replace(/[\r\n]/g, '').length;
                fpCounter.textContent = len + ' / ' + fpMaxChars;
                fpCounter.classList.toggle('arlo-opt-charcount--warn', len >= fpMaxChars);
              }
              if (trimmed) {
                // Let the customer know their message didn't fully fit.
                var prevErr = err.textContent;
                err.textContent = best > 0
                  ? 'Your message was shortened to fit the space'
                  : 'That message is too long to fit';
                err.style.display = 'block';
                setTimeout(function () {
                  err.style.display = 'none';
                  err.textContent = prevErr;
                }, 3500);
              } else {
                err.style.display = 'none';
              }
            });
          }
        });

        fpInput.addEventListener('input', function () {
          if (_fpAdjusting) return; // trim-to-fit loop is driving the DOM
          // 2026-08-07 REWRITE — three fixes in one (see cart-bug history):
          // 1. Sanitize IN PLACE per text node (sanitizeEditableInPlace) so
          //    normalising a heart emoji no longer rewrites textContent and
          //    destroys the customer's line breaks / formatting.
          // 2. Read text via innerText (preserves line breaks; textContent
          //    concatenates <div> lines without them).
          // 3. Canvas overflow = try-then-revert: measure the REAL height
          //    after the edit; only revert if it genuinely overflows.
          sanitizeEditableInPlace(fpInput);

          var text = fpIsCanvas ? _fpText() : _fpText().replace(/[\r\n]/g, '');

          // maxChars trim — rare safety net (keydown + paste already limit).
          // Falls back to a flat rewrite; acceptable for this edge case.
          if (fpMaxChars && text.replace(/[\r\n]/g, '').length > fpMaxChars) {
            text = text.slice(0, fpMaxChars);
            fpInput.textContent = text;
            var range = document.createRange();
            var sel = window.getSelection();
            range.selectNodeContents(fpInput);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }

          // Canvas: revert edits that genuinely overflow the artboard.
          if (fpIsCanvas) {
            var capacity = fpFlex.clientHeight || fpInput.clientHeight;
            if (_circleCenter) _circleCenter(); // circle: re-centre before measuring
            if (_fpUsedH() > capacity + CANVAS_OVERFLOW_TOLERANCE ||
                (_circleInside && !_circleInside())) {
              _fpRevert();
              if (_circleCenter) _circleCenter();
              text = _fpText();
            } else {
              _fpSnapshot();
            }
            if (!_circleCenter) _fpCentre(); // rectangles: centre on the text
          }

          fpHidden.value = fpUpper ? text.toUpperCase() : text;
          fpInput.classList.remove('arlo-opt-error');
          err.style.display = 'none';
          if (fpCounter) {
            var len = text.replace(/[\r\n]/g, '').length;
            fpCounter.textContent = len + ' / ' + fpMaxChars;
            fpCounter.classList.toggle('arlo-opt-charcount--warn', len >= fpMaxChars);
          }
        });

        err.textContent = 'Please enter your text';

        // Always show a step label for canvas-png inputs — fall back to a generic label
        // so the step badge is always registered with the coordinator even if no label is set.
        wrap.appendChild(makeLabel(Object.assign({}, opt, { label: opt.label || 'Personalised message', required: false })));

        // Track the currently selected font — shared by font buttons + preview button.
        // Starts as the first (or only) font; updated when a font button is clicked.
        var currentFontConfig = resolvedFonts.length ? resolvedFonts[0] : null;

        // Font selector buttons — only shown for Case 1 (multiple fonts configured)
        if (resolvedFonts.length > 1) {
          fpFontHidden = document.createElement('input');
          fpFontHidden.type = 'hidden';
          fpFontHidden.name = 'properties[' + fpFontPropLabel + ']';
          fpFontHidden.value = resolvedFonts[0].displayName || resolvedFonts[0].name;

          var fpFontRow = document.createElement('div');
          fpFontRow.className = 'arlo-opt-fp-fonts';

          resolvedFonts.forEach(function (f, fi) {
            var css = f.id; // use font id — matches the name registered by arlo-font-selector.js via FontFace API
            var fontBtn = document.createElement('button');
            fontBtn.type = 'button';
            fontBtn.className = 'arlo-opt-fp-font-btn' + (fi === 0 ? ' active' : '');
            fontBtn.title = f.displayName || f.name;

            var sample = document.createElement('span');
            sample.className = 'arlo-opt-fp-font-sample';
            sample.style.fontFamily = "'" + css + "'";
            sample.textContent = 'Aa';

            var lbl = document.createElement('span');
            lbl.className = 'arlo-opt-fp-font-name';
            lbl.textContent = f.displayName || f.name;

            fontBtn.appendChild(sample);
            fontBtn.appendChild(lbl);

            fontBtn.addEventListener('click', function () {
              fpFontRow.querySelectorAll('.arlo-opt-fp-font-btn').forEach(function (b) { b.classList.remove('active'); });
              fontBtn.classList.add('active');
              fpInput.style.fontFamily = "'" + css + "'";
              if (fpFontHidden) fpFontHidden.value = f.displayName || f.name;
              currentFontConfig = f; // keep preview button in sync
              _fpCentre(); // new font = new line widths
            });

            fpFontRow.appendChild(fontBtn);
          });

          wrap.appendChild(fpFontRow);
          wrap.appendChild(fpFontHidden);
        }

        wrap.appendChild(fpWrap || fpInput);
        if (fpCounter) wrap.appendChild(fpCounter);


        wrap.appendChild(fpHidden);
        wrap.appendChild(err);

      } else if (opt.type === 'uploadcare') {
        // Move the uploader slot into this step so it appears at the right position.
        var ucSlot = document.getElementById('arlo-uploader-slot');
        if (ucSlot) {
          ucSlot.style.display = '';
          wrap.appendChild(ucSlot);
        }
        // No hidden input needed here — arlo-upload-value is inside the slot.

      } else if (opt.type === 'checkbox') {
        // Checkbox as a hidden input + visual toggle
        var cbWrap = document.createElement('label');
        cbWrap.className = 'arlo-opt-checkbox-wrap';

        var cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.className = 'arlo-opt-checkbox';

        // Hidden input that carries the value — always present so Shopify sees it
        var cbHidden = document.createElement('input');
        cbHidden.type = 'hidden';
        cbHidden.name = 'properties[' + opt.label + ']';
        cbHidden.value = '';

        err.textContent = 'This option is required';

        cb.addEventListener('change', function () {
          cbHidden.value = cb.checked ? (opt.checkedValue || 'Yes') : '';
          cbWrap.classList.remove('arlo-opt-error');
          err.style.display = 'none';
        });

        var cbLabel = document.createElement('span');
        cbLabel.className = 'arlo-opt-checkbox-label';
        cbLabel.textContent = opt.label;
        if (opt.required) {
          var starCb = document.createElement('span');
          starCb.className = 'arlo-opt-required';
          starCb.textContent = ' *';
          cbLabel.appendChild(starCb);
        }

        cbWrap.appendChild(cb);
        cbWrap.appendChild(cbLabel);

        // Add-on price + markdown, pulled from the linked variant's live Shopify
        // price (now) and compare-at (was). The bag is charged at its variant
        // price on add, so the display and the charge come from one source.
        if (!opt.hidePrice && opt.linkedVariantPrice != null) {
          cbWrap.style.display = 'flex';
          cbWrap.style.width = '100%';
          cbWrap.style.boxSizing = 'border-box';
          var cbPrice = document.createElement('span');
          cbPrice.className = 'arlo-opt-checkbox-price';
          cbPrice.style.cssText = 'margin-left:auto;white-space:nowrap';
          var cbCmp = opt.linkedVariantCompareAt || 0;
          if (cbCmp > opt.linkedVariantPrice) {
            var wasEl = document.createElement('span');
            wasEl.textContent = '$' + (cbCmp / 100).toFixed(2);
            wasEl.style.cssText = 'text-decoration:line-through;opacity:.55;margin-right:6px';
            cbPrice.appendChild(wasEl);
          }
          var nowEl = document.createElement('span');
          nowEl.textContent = '$' + (opt.linkedVariantPrice / 100).toFixed(2);
          nowEl.style.fontWeight = '600';
          cbPrice.appendChild(nowEl);
          cbWrap.appendChild(cbPrice);
        }

        wrap.appendChild(cbWrap);
        wrap.appendChild(cbHidden);
        wrap.appendChild(err);
      }

      container.appendChild(wrap);
    });

    // Insert before the qty+ATC wrapper (keeps options full-width, above the flex row)
    var qtyWrapper = form.querySelector('.product-form__quantity-and-buy-buttons-wrapper');
    if (qtyWrapper && qtyWrapper.parentNode) {
      qtyWrapper.parentNode.insertBefore(container, qtyWrapper);
    } else {
      // Fallback: before the ATC button's parent
      var atcBtn = form.querySelector('[data-add-to-cart]') ||
                   form.querySelector('[name="add"]') ||
                   form.querySelector('[type="submit"]');
      if (atcBtn && atcBtn.parentNode) {
        atcBtn.parentNode.insertBefore(container, atcBtn);
      } else {
        form.appendChild(container);
      }
    }

    return container;
  }

  // ── Validate required fields ──────────────────────────────────────────────────
  function validateOptions(options) {
    var valid = true;
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return true;

    options.forEach(function (opt) {
      if (!opt.required) return;
      var wrap = container.querySelector('[data-opt-id="' + opt.id + '"]');
      if (!wrap) return;

      var errEl = wrap.querySelector('.arlo-opt-error-msg');

      if (opt.type === 'select') {
        var sel = wrap.querySelector('select');
        if (sel && !sel.value) {
          sel.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          if (valid) sel.focus();
          valid = false;
        }
      } else if (opt.type === 'buttons') {
        var bWrap = wrap.querySelector('.arlo-opt-buttons');
        var bHidden = wrap.querySelector('input[type="hidden"]');
        if (bHidden && !bHidden.value) {
          if (bWrap) bWrap.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          valid = false;
        }
      } else if (opt.type === 'tiles') {
        var tWrap = wrap.querySelector('.arlo-opt-tiles');
        var tHidden = wrap.querySelector('input[type="hidden"]');
        if (tHidden && !tHidden.value) {
          if (tWrap) tWrap.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          valid = false;
        }
      } else if (opt.type === 'monogram') {
        var mHidden = wrap.querySelector('input[type="hidden"]');
        var mInput = wrap.querySelector('.arlo-opt-mono-input');
        if (mHidden && !mHidden.value.trim()) {
          if (mInput) mInput.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          if (valid && mInput) mInput.focus();
          valid = false;
        }
      } else if (opt.type === 'checkbox') {
        var cb = wrap.querySelector('input[type="checkbox"]');
        var cbWrapEl = wrap.querySelector('.arlo-opt-checkbox-wrap');
        if (cb && !cb.checked) {
          if (cbWrapEl) cbWrapEl.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          valid = false;
        }
      } else if (opt.type === 'font-previewer') {
        var fpEl = wrap.querySelector('.arlo-opt-fp-input');
        var fpH = wrap.querySelector('input[type="hidden"][name^="properties["]');
        if (fpEl && !fpEl.textContent.trim()) {
          fpEl.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          if (valid) fpEl.focus();
          valid = false;
        }
      }
    });

    // Photo uploader — when required, EVERY slot must be filled (not just one).
    // arlo-options intercepts the submit and bypasses the uploader's own check,
    // so we validate it here against the current (possibly per-variant) max count.
    var ucOpt = options.find(function (o) { return o.type === 'uploadcare' && o.required; });
    if (ucOpt) {
      var uploaderRoot = document.getElementById('arlo-uploader');
      if (uploaderRoot) {
        var need = parseInt(uploaderRoot.dataset.maxImages, 10) || 1;
        var have;
        if (need <= 1) {
          var single = document.getElementById('arlo-upload-value');
          have = (single && single.value) ? 1 : 0;
        } else {
          // One properties[Photo N] input is written per completed upload.
          have = document.querySelectorAll('.arlo-photo-prop[name^="properties[Photo"]').length;
        }
        if (have < need) {
          valid = false;
          showUploaderError(uploaderRoot, need, have);
          uploaderRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          clearUploaderError(uploaderRoot);
        }
      }
    }

    return valid;
  }

  function showUploaderError(root, need, have) {
    var msg = document.getElementById('arlo-uploader-req-msg');
    if (!msg) {
      msg = document.createElement('div');
      msg.id = 'arlo-uploader-req-msg';
      msg.style.cssText = 'color:var(--color-error,#c0392b);font-size:13px;font-weight:600;margin-top:8px';
      (root.parentNode || root).insertBefore(msg, root.nextSibling);
    }
    // 2026-07-26: no live count here anymore. The old "(X of N added)" counted
    // hidden properties[Photo N] inputs (written only on COMPLETED uploads),
    // while the uploader's own header counts selected slots — the two could
    // legitimately disagree mid-upload (header "2 of 8", this line "0 of 8"),
    // which read as a bug. The header already shows progress; this line only
    // needs to say what's required.
    msg.textContent = need === 1
      ? 'Please upload your photo to continue.'
      : 'Please upload all ' + need + ' photos to continue.';
    msg.style.display = 'block';
  }

  function clearUploaderError(root) {
    var msg = document.getElementById('arlo-uploader-req-msg');
    if (msg) msg.style.display = 'none';
  }

  // ── Suppress native Shopify & app option selectors for Arlo-configured products ──
  function suppressNativeOptions() {
    if (document.getElementById('arlo-options-suppress')) return;
    var s = document.createElement('style');
    s.id = 'arlo-options-suppress';
    // All rules scoped to html.arlo-managed so they stop applying the moment
    // that class is removed (e.g. AJAX navigation to a non-Arlo product).
    s.textContent = [
      // Artisan theme custom option blocks — outer wrapper (label + input)
      'html.arlo-managed .product__controls-group--text { display: none !important; }',
      'html.arlo-managed .product__controls-group--textarea { display: none !important; }',
      'html.arlo-managed .product__controls-group--checkbox { display: none !important; }',
      'html.arlo-managed .product__controls-group--list { display: none !important; }',
      // Inner input area
      'html.arlo-managed .product__custom-option { display: none !important; }',
      'html.arlo-managed .product__custom-option-instructions { display: none !important; }',
      // Hidden form inputs (prevent blank order properties)
      'html.arlo-managed .product-form__custom-option { display: none !important; }',
      // Old arlo-font-selector.js font picker
      'html.arlo-managed .arlo-font-selector { display: none !important; }',
      // Common Shopify app option injectors
      'html.arlo-managed .infiniteoptions-container { display: none !important; }',
      'html.arlo-managed [class*="iO-option"] { display: none !important; }',
      'html.arlo-managed .BOLD_options { display: none !important; }',
      'html.arlo-managed .bold-options { display: none !important; }',
      'html.arlo-managed .gpotf-options-form { display: none !important; }',
      'html.arlo-managed [id^="shoppad-"] { display: none !important; }',
      'html.arlo-managed .sc-product-option { display: none !important; }',
      // SPB app option fields (label uses spb-productdesc*, input uses spb-productopt*)
      'html.arlo-managed [class*="spb-product"] { display: none !important; }',
      'html.arlo-managed .spb-helptext { display: none !important; }',
    ].join('\n');
    document.head.appendChild(s);

    // Hide and disable any third-party app property inputs not inside our own container.
    // Works regardless of what CSS classes the app uses (SPB, InfiniteOptions, etc.)
    document.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if (el.closest('#' + CONTAINER_ID)) return; // skip our own inputs
      if (el.closest('#arlo-uploader-slot')) return; // skip photo uploader hidden input — buildFields will reposition it
      el.disabled = true;
      el.removeAttribute('name');
    });

    // Nuclear suppression: hide any direct child of the form that contains
    // legacy properties[] inputs (catches fieldset/span/div wrappers regardless of class)
    var productForm = document.querySelector('form[action*="/cart/add"], form[data-product-form]');
    if (productForm) {
      Array.prototype.forEach.call(productForm.children, function (child) {
        if (child.id === CONTAINER_ID) return; // never hide our own container
        if (child.id === 'arlo-uploader-slot') return; // skip — buildFields will reposition this
        if (child.querySelector('[name^="properties["]') ||
            child.querySelector('[data-custom-option-target]')) {
          child.style.cssText = 'display:none!important';
        }
      });
    }

    // Also disable Artisan hidden form inputs
    document.querySelectorAll('[data-custom-option-target]').forEach(function (el) {
      el.disabled = true;
      el.removeAttribute('name');
    });

    // Remove empty <label> elements left by native Shopify/Artisan option blocks
    document.querySelectorAll('fieldset label').forEach(function (lbl) {
      if (!lbl.textContent.trim() && !lbl.querySelector('input, select, textarea')) {
        lbl.remove();
      }
    });
  }

  // ── Collect all line-item properties for the main product ────────────────────
  // Gathers hidden properties[...] inputs from BOTH the arlo-options container AND
  // the cart form itself. The photo uploader (arlo-photo-uploader.js) appends its
  // per-photo display props (properties[Photo 1], [Photo 2], …), the multi-image
  // group URL (_Photo Group), and the circle flag directly onto the form — outside
  // our container. If we only read the container, multi-image cart previews are lost.
  function collectMainProperties() {
    var props = {};
    var container = document.getElementById(CONTAINER_ID);
    if (container) {
      // 2026-07-26 fix: was input[type="hidden"] only — but the DROPDOWN
      // option type renders as a live <select name="properties[...]"> with
      // no hidden mirror input, so its selected value was silently dropped
      // from the cart (every other type — buttons, font-previewer, photos —
      // writes a hidden input and was fine). Collect selects and textareas
      // too; the m[1].trim() guard keeps the known Shopify-400 "empty label"
      // trap out (see the fallback-label rule used elsewhere in this file).
      container.querySelectorAll(
        'input[type="hidden"][name^="properties["], select[name^="properties["], textarea[name^="properties["]'
      ).forEach(function (inp) {
        var m = inp.name.match(/^properties\[(.+)\]$/);
        if (m && m[1].trim() && inp.value) props[m[1]] = inp.value;
      });
    }
    var form = document.querySelector('form[action*="/cart/add"], form[data-product-form]');
    if (form) {
      form.querySelectorAll('.arlo-photo-prop[name^="properties["], #arlo-upload-value[name^="properties["]').forEach(function (inp) {
        var m = inp.name.match(/^properties\[(.+)\]$/);
        if (m && inp.value) props[m[1]] = inp.value;
      });
    }
    return props;
  }

  // 2026-07-23 fix: reopening the cart (via the header cart icon) sometimes
  // showed a stale snapshot missing items that were genuinely in the cart.
  // Cause: theme.js's openQuickCart() (triggered by clicking the header icon)
  // is PURELY visual — it just toggles a class, activates the focus trap, and
  // disables body scroll. It never re-fetches anything. The drawer just shows
  // whatever HTML happened to be sitting there from the last refresh. If
  // several adds happened close together, their independent, un-sequenced
  // section-refresh fetches can resolve out of order — whichever response
  // lands LAST wins, even if it was triggered FIRST and reflects an earlier,
  // smaller cart state. Reopening later then shows that stale snapshot with
  // no further refresh to correct it. Fix: force a genuine refresh every time
  // the header cart icon is clicked, not just when our own add-to-cart flow
  // triggers one — capture phase so this runs regardless of what else is
  // listening on the same click.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-header-cart]') && window.emit) {
      window.emit('quick-cart:updated');
    }
  }, true);

  // 2026-07-23 fix: "Your cart is Empty" flashes up before the added item(s)
  // appear. Cause: sections/quick-cart.liquid is server-rendered once, at
  // whatever cart state existed when the CURRENT PAGE loaded — if the cart was
  // empty at that point, the empty-state markup is baked into the page's HTML.
  // Adding an item via fetch() doesn't reload the page, so that stale HTML is
  // still sitting in the DOM. 'quick-cart:updated' kicks off theme.js's async
  // section-refresh fetch (to pull fresh HTML reflecting the new cart state)
  // but does NOT wait for it — every call site here used to emit
  // 'quick-cart:open' on the very next line, opening the drawer immediately
  // against whatever HTML was already there (the stale empty state) for
  // however long the refresh's network round-trip took.
  // Fix: watch for the refreshed HTML actually landing (a DOM mutation inside
  // .quick-cart) before opening, with a safety-net timeout so a slow/failed
  // refresh can never block the drawer from opening at all.
  function openQuickCartWhenReady() {
    var wrapper = document.querySelector('.quick-cart');
    if (!wrapper || !window.emit) {
      if (window.emit) window.emit('quick-cart:open');
      return;
    }

    var opened = false;
    function doOpen() {
      if (opened) return;
      opened = true;
      observer.disconnect();
      clearTimeout(safety);
      window.emit('quick-cart:open');
      // 2026-07-23 fix: this is the confirmed moment the refreshed cart
      // section HTML has actually landed — the exact signal custom.js's
      // "Others Also Bought" rebuild needs instead of guessing a fixed delay
      // (see arloRebuildCartRecs in custom.js for the full explanation).
      if (window.arloRebuildCartRecs) window.arloRebuildCartRecs();
    }

    // 2026-07-23 fix: a single section-HTML swap can fire this callback more
    // than once (the browser can split one logical replace into several
    // mutation records) — reacting to the very first callback risked opening
    // / rebuilding recs against a still-mid-swap DOM rather than the final
    // result. Debounce to a short quiet period instead of acting on the
    // first flicker; the safety-net timeout below still guarantees an upper
    // bound regardless of how long mutations keep trickling in.
    var settleTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(doOpen, 150);
    });
    observer.observe(wrapper, { childList: true, subtree: true });

    window.emit('quick-cart:updated');
    // 2026-07-23 fix: the header's own cart-icon count badge (the little
    // number on the mini-cart icon site-wide) never updated after adding
    // through our custom fetch-based ATC flow — it only ever changed after a
    // full page reload. Root cause: theme.js's header component listens
    // specifically for 'cart:updated' (not 'quick-cart:updated', which only
    // refreshes the drawer) to know when to re-fetch and swap in fresh header
    // HTML with the correct count. Our flow never emitted that event at all,
    // since it deliberately bypasses the theme's native add-to-cart pub/sub
    // to submit only our own container's properties. Emitting it here too
    // (theme.js's own listener doesn't care about the payload, it just
    // re-fetches the header section regardless) fixes the header badge
    // without otherwise changing anything about our custom ATC flow.
    window.emit('cart:updated');

    // Never block opening indefinitely if the refresh doesn't produce a DOM
    // mutation for some reason (e.g. item count unchanged edge case).
    var safety = setTimeout(doOpen, 1200);
  }

  // ── Add linked products to cart for checked checkboxes / selected buttons ─────
  function addLinkedProducts(options) {
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return Promise.resolve();

    var promises = [];
    options.forEach(function (opt) {
      var wrap = container.querySelector('[data-opt-id="' + opt.id + '"]');
      if (!wrap) return;

      if (opt.type === 'checkbox') {
        // Checkbox: option-level linkedVariantId only
        if (!opt.linkedVariantId) return;
        var cb = wrap.querySelector('input[type="checkbox"]');
        if (!cb || !cb.checked) return;
        promises.push(fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: opt.linkedVariantId, quantity: opt.linkedVariantQty || 1 }),
        }).catch(function (err) { console.warn('[ArloOptions] Failed to add linked product:', err); }));

      } else if (opt.type === 'buttons') {
        // Buttons: per-button-value linkedVariantId takes priority; fall back to option-level
        var activeBtn = wrap.querySelector('.arlo-opt-button.active');
        if (!activeBtn) return;
        var bv = activeBtn._arloBv || {};
        var variantId = bv.linkedVariantId || opt.linkedVariantId;
        if (!variantId) return;
        var qty = parseInt(bv.linkedVariantQty, 10) || opt.linkedVariantQty || 1;
        promises.push(fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        }).catch(function (err) { console.warn('[ArloOptions] Failed to add linked product:', err); }));
      }
    });

    return Promise.all(promises);
  }

  // ── Hook cart form ────────────────────────────────────────────────────────────
  function hookForm(options, form) {
    var canvasOptions = options.filter(function (o) {
      return o.type === 'font-previewer' && o.outputFormat === 'canvas-png';
    });
    var hasCanvas = canvasOptions.length > 0;
    var _linkedSubmitPending = false;

    function handleSubmit(e, isBtn) {
      // Skip if we re-dispatched after linked products finished — let theme handle it
      if (_linkedSubmitPending) return;

      if (!validateOptions(options)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var container = document.getElementById(CONTAINER_ID);
        if (container) container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var hasLinked = options.some(function (opt) {
        var c = document.getElementById(CONTAINER_ID);
        if (!c) return false;
        var wrap = c.querySelector('[data-opt-id="' + opt.id + '"]');
        if (!wrap) return false;
        if (opt.type === 'checkbox') {
          if (!opt.linkedVariantId) return false;
          var cb = wrap.querySelector('input[type="checkbox"]');
          return cb && cb.checked;
        }
        if (opt.type === 'buttons') {
          // Check button-value-level OR option-level linked variant
          var activeBtn = wrap.querySelector('.arlo-opt-button.active');
          if (!activeBtn) return false;
          var bv = activeBtn._arloBv || {};
          return !!(bv.linkedVariantId || opt.linkedVariantId);
        }
        return false;
      });

      if (hasCanvas) {
        e.preventDefault();
        e.stopImmediatePropagation();

        var atcBtnC = form.querySelector('[data-add-to-cart]');
        if (atcBtnC) atcBtnC.classList.add('loading');

        // Generate the artwork PNG(s) FIRST (sets the hidden property inputs),
        // THEN add the main product + any linked products together in one batch
        // and open the cart once. This avoids the earlier double-open glitch where
        // the linked product was added, the cart popped open, and then a separate
        // form.submit() for the main product closed and re-opened it.
        runCanvasCapture(options, form, canvasOptions, function () {
          var variantEl = form.querySelector('[name="id"]');
          var mainVariantId = variantEl ? parseInt(variantEl.value, 10) : null;
          var qtyEl = form.querySelector('[name="quantity"]');
          var mainQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

          var mainProperties = collectMainProperties();

          var mainBody = { id: mainVariantId, quantity: mainQty };
          if (Object.keys(mainProperties).length) mainBody.properties = mainProperties;

          // Add the main product first, then the linked products, so the main
          // (personalised) item sits at the top of the cart.
          var mainPromise = mainVariantId ? fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mainBody),
          }).catch(function (err) { console.warn('[ArloOptions] Failed to add main product:', err); }) : Promise.resolve();

          mainPromise.then(function () {
            return addLinkedProducts(options);
          }).then(function () {
            if (atcBtnC) atcBtnC.classList.remove('loading');
            openQuickCartWhenReady();
            document.dispatchEvent(new CustomEvent('cart:item-added'));
          }).catch(function (err) {
            if (atcBtnC) atcBtnC.classList.remove('loading');
            console.warn('[ArloOptions] cart add failed:', err);
          });
        });
        return;
      }

      if (hasLinked) {
        // Add the main product and all linked products directly via fetch,
        // then trigger cart refresh manually. We avoid re-dispatching the
        // form submit because the theme's variant-lookup chain is fragile for
        // single-variant / single-option products.
        e.preventDefault();
        e.stopImmediatePropagation();

        // Collect main product info now (before async ops can change it)
        var variantEl = form.querySelector('[name="id"]');
        var mainVariantId = variantEl ? parseInt(variantEl.value, 10) : null;
        var qtyEl = form.querySelector('[name="quantity"]');
        var mainQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

        // Collect arlo-options + photo-uploader hidden property inputs
        var mainProperties = collectMainProperties();

        // Show ATC loading state so UX matches normal cart add
        var atcBtn = form.querySelector('[data-add-to-cart]');
        if (atcBtn) atcBtn.classList.add('loading');

        var mainBody = { id: mainVariantId, quantity: mainQty };
        if (Object.keys(mainProperties).length) mainBody.properties = mainProperties;

        // 2026-07-23 fix: the main product and linked product(s) used to be added
        // via two separate /cart/add.js calls fired in PARALLEL (Promise.all).
        // Shopify's cart endpoint isn't safe for that — each request reads the
        // cart's current state, adds its item, and writes the whole cart back, so
        // two near-simultaneous requests can each read the same "before" state and
        // clobber each other's write, silently dropping one item even though both
        // requests reported success. This is worst on a genuinely empty cart (no
        // session/cookie yet) — two parallel first-adds can even fork into two
        // separate cart sessions, each holding only one item, matching "sometimes
        // just the bag, sometimes just the keytag." Fixed by waiting for the main
        // add to fully complete before starting the linked-product add, the same
        // sequential pattern already used safely in the canvas-personalisation
        // branch above.
        var mainPromise = mainVariantId ? fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(mainBody),
        }).catch(function (err) {
          console.warn('[ArloOptions] Failed to add main product:', err);
        }) : Promise.resolve();

        mainPromise.then(function () {
          return addLinkedProducts(options);
        }).then(function () {
          if (atcBtn) atcBtn.classList.remove('loading');
          openQuickCartWhenReady();
          // Fire DOM event to trigger recs refresh + reset personalisation
          document.dispatchEvent(new CustomEvent('cart:item-added'));
        }).catch(function (err) {
          if (atcBtn) atcBtn.classList.remove('loading');
          console.warn('[ArloOptions] cart add failed:', err);
        });

        return;
      }

      // No linked products, no canvas — use fetch so we submit ONLY our container
      // properties and avoid any spurious inputs the native form might include.
      e.preventDefault();
      e.stopImmediatePropagation();

      var variantEl = form.querySelector('[name="id"]');
      var mainVariantId = variantEl ? parseInt(variantEl.value, 10) : null;
      var qtyEl = form.querySelector('[name="quantity"]');
      var mainQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

      var mainProperties = collectMainProperties();

      var atcBtn2 = form.querySelector('[data-add-to-cart]');
      if (atcBtn2) atcBtn2.classList.add('loading');

      var mainBody2 = { id: mainVariantId, quantity: mainQty };
      if (Object.keys(mainProperties).length) mainBody2.properties = mainProperties;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mainBody2),
      }).then(function () {
        if (atcBtn2) atcBtn2.classList.remove('loading');
        openQuickCartWhenReady();
        document.dispatchEvent(new CustomEvent('cart:item-added'));
      }).catch(function (err) {
        if (atcBtn2) atcBtn2.classList.remove('loading');
        console.warn('[ArloOptions] cart add failed:', err);
      });
    }

    form.addEventListener('submit', function (e) { handleSubmit(e, false); }, true);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      handleSubmit(e, true);
    }, true);
  }

  // ── Canvas PNG capture ────────────────────────────────────────────────────────
  var MM_PER_INCH = 25.4;

  function runCanvasCapture(options, form, canvasOptions, onComplete) {
    // onComplete: called once all artwork PNGs are generated and their hidden
    // property inputs are populated. Falls back to a native form submit if not
    // provided (kept for safety / backward-compat).
    var done = typeof onComplete === 'function' ? onComplete : function () { form.submit(); };
    var config = window.ArloProductConfig || {};
    var ucKey = config.uploadcareCanvasPngPublicKey || '';
    var fontMap = {};
    (config.fonts || []).forEach(function (f) { fontMap[f.id] = f; });

    var statusEl = getOrCreateStatusEl();
    showStatus(statusEl, 'Preparing your artwork…', 'loading');

    // Build a list of canvas render tasks
    var tasks = [];
    canvasOptions.forEach(function (opt) {
      var container = document.getElementById(CONTAINER_ID);
      var wrap = container ? container.querySelector('[data-opt-id="' + opt.id + '"]') : null;
      var fpEl = wrap ? wrap.querySelector('.arlo-opt-fp-input') : null;
      // [data-required] distinguishes the TEXT property input (always carries
      // data-required="true|false") from the font-name hidden input (which has
      // no such attribute and, for multi-font products, comes FIRST in the DOM
      // — a bare first-match here would overwrite the font name with the
      // artwork URL). Latent bug found 2026-07-26; no shipped product had the
      // canvas-png + multi-font combination that triggers it.
      var fpH  = wrap ? wrap.querySelector('input[type="hidden"][name^="properties["][data-required]') : null;
      // 2026-08-07: extract visual lines by DOM walk (extractVisualLines), NOT
      // innerText — innerText emitted DOUBLE newlines for blank lines
      // (<div><br></div>), so PNGs got bigger gaps than the preview showed.
      // Remove blank lines after the message first (and re-centre the box), so
      // the PNG — especially a round box's screen snapshot — is centred.
      if (fpEl && fpEl._arloTidyEnd) fpEl._arloTidyEnd();
      var text = fpEl ? extractVisualLines(fpEl).join('\n') : '';
      if (opt.uppercase) text = text.toUpperCase();
      if (!text || !fpH) return;

      // Round boxes: snapshot the exact on-screen lines + positions so the PNG
      // is painted from what the customer saw (no second wrap). null → the
      // normal text path is used as a fallback.
      var layout = null;
      if (opt.canvasSettings && opt.canvasSettings.shape === 'circle' && fpEl) {
        layout = extractCircleLayout(fpEl, fpEl.parentNode);
        if (layout && opt.uppercase) {
          layout.lines.forEach(function (ln) { ln.text = ln.text.toUpperCase(); });
        }
      }

      // Resolve active font — check which font button is active if multiple fonts
      var activeFontId = null;
      if (wrap) {
        var activeBtn = wrap.querySelector('.arlo-opt-fp-font-btn.active');
        if (activeBtn) {
          // Find the font hidden input (stores displayName, not id) — use button index instead
          var allBtns = wrap.querySelectorAll('.arlo-opt-fp-font-btn');
          var btnIdx = Array.prototype.indexOf.call(allBtns, activeBtn);
          var fontIds = opt.fonts || [];
          if (btnIdx >= 0 && btnIdx < fontIds.length) activeFontId = fontIds[btnIdx];
        }
      }
      if (!activeFontId) activeFontId = (opt.fonts || [])[0];

      tasks.push({ text: text, fpH: fpH, fontId: activeFontId, cs: opt.canvasSettings || {}, layout: layout });
    });

    if (!tasks.length) { hideStatus(statusEl); done(); return; }

    (function runTasks(idx) {
      if (idx >= tasks.length) {
        hideStatus(statusEl);
        done();
        return;
      }
      var task = tasks[idx];
      var fontConfig = fontMap[task.fontId];

      showStatus(statusEl, 'Loading font…', 'loading');
      loadCanvasFont(fontConfig).then(function () {
        showStatus(statusEl, 'Generating artwork…', 'loading');
        var canvas = renderTextToCanvas(task.text, fontConfig, task.cs, task.layout);

        if (ucKey) {
          showStatus(statusEl, 'Uploading artwork…', 'loading');
          canvasToBlob(canvas).then(function (blob) {
            var filename = 'artwork-' + Date.now() + '-' + idx + '.png';
            uploadToUploadcare(blob, ucKey, filename).then(function (url) {
              task.fpH.value = url;
              runTasks(idx + 1);
            }).catch(function (err) {
              console.error('[Arlo] Upload error:', err);
              // Proceed with raw text if upload fails
              runTasks(idx + 1);
            });
          });
        } else {
          // No UC key — embed as data URL (useful for testing)
          task.fpH.value = canvas.toDataURL('image/png');
          runTasks(idx + 1);
        }
      }).catch(function () {
        runTasks(idx + 1);
      });
    })(0);
  }

  function loadCanvasFont(fontConfig) {
    if (!fontConfig || !fontConfig.files || !fontConfig.files.length) {
      return Promise.resolve();
    }
    if (document.fonts && document.fonts.check('12px \'' + fontConfig.id + '\'')) {
      return Promise.resolve();
    }
    var files = fontConfig.files;
    var idx = 0;
    function tryNext() {
      if (idx >= files.length) return Promise.reject(new Error('No font file loaded'));
      var file = files[idx++];
      // Same per-font Size % as the @font-face in font-preview.css, so the PNG
      // matches the screen even when this face is the one the browser uses.
      var pct = Number(fontConfig.sizePct);
      var desc = pct && pct !== 100 ? { sizeAdjust: pct + '%' } : {};
      var face = new FontFace(fontConfig.id, 'url(\'' + file.url + '\')', desc);
      return face.load().then(function (loaded) {
        document.fonts.add(loaded);
        return document.fonts.ready;
      }).catch(tryNext);
    }
    return tryNext();
  }

  // ♥ (U+2665) has no glyph in any of our uploaded monogram fonts, so the browser
  // silently falls back to a system font for just that character when it hits
  // ctx.fillText() — and on most platforms that fallback is a COLOR emoji font
  // (bitmap/COLR glyphs), which Canvas2D can NEVER recolor via ctx.fillStyle. No
  // amount of CSS (font-variant-emoji, variation selectors) fixes this, because
  // those only affect text layout/selection, not what Canvas2D actually paints.
  // Fix: never ask the font to render ♥ at all — draw it ourselves as a filled
  // vector path in the exact same fillStyle as the rest of the text, so it can
  // only ever come out as plain black (or whatever the monogram color is).
  var HEART_CHAR = '♥';

  // Customers can type or paste ANY emoji into the free-text personalisation
  // field (font-previewer), not just the hand-picked ♥ button. Every one of
  // those would hit the exact same color-emoji-font problem described above
  // when it reaches ctx.fillText() on the canvas-png output — Canvas2D can
  // never recolor a color/bitmap emoji glyph, no matter what fillStyle is set.
  // Rather than hand-drawing every possible emoji shape, input is normalised
  // as it's typed/pasted: recognised heart variants (❤️, ❤, ♡, 💕, 💖, 💗, 💓,
  // 💞, 💘) are swapped for our own plain-text heart (HEART_CHAR — drawn in
  // solid black via drawHeartGlyph above), and any other emoji is stripped
  // outright so it can never reach the canvas as color.
  var HEART_VARIANTS_RE = /❤️?|♡️?|\uD83D[\uDC93\uDC95\uDC96\uDC97\uDC98\uDC9D\uDC9E]/g;
  // Broad emoji/pictograph ranges (astral surrogate-pair emoji, misc symbols,
  // dingbats, arrows, extra symbols, variation selectors, zero-width joiners)
  // — anything left after the heart swap above gets removed rather than risk
  // it rendering in color on the PNG.
  var OTHER_EMOJI_RE = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[☀-➿←-⇿⬀-⯿]|️|‍/g;

  function sanitizePersonalisationText(text) {
    text = text.replace(HEART_VARIANTS_RE, HEART_CHAR);
    text = text.replace(OTHER_EMOJI_RE, function (ch) { return ch === HEART_CHAR ? ch : ''; });
    return text;
  }

  // 2026-08-07: sanitize contenteditable content WITHOUT destroying its line
  // structure. The old approach read+rewrote fpInput.textContent — but a
  // contenteditable stores each line as a <div>, and textContent concatenates
  // them WITHOUT newlines. So the moment a customer typed a heart emoji (the
  // only thing that triggers a rewrite), their carefully formatted lines
  // collapsed into one string and re-wrapped from scratch — "adding a heart
  // scrambled my layout". This walks individual TEXT NODES instead: the
  // div/br line structure is never touched, and the caret is restored inside
  // the node that changed (where the emoji was just typed).
  function sanitizeEditableInPlace(root) {
    var changed = false;
    var sel = window.getSelection ? window.getSelection() : null;
    var caretNode = (sel && sel.rangeCount) ? sel.getRangeAt(0).endContainer : null;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    var n;
    while ((n = walker.nextNode())) {
      var v = n.nodeValue;
      var nv = sanitizePersonalisationText(v);
      if (nv !== v) {
        var atCaret = (n === caretNode);
        n.nodeValue = nv;
        changed = true;
        if (atCaret && sel) {
          try {
            var r = document.createRange();
            r.setStart(n, nv.length);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
          } catch (e) {}
        }
      }
    }
    return changed;
  }


  // 2026-08-07: extract the box's content as VISUAL lines by walking the DOM
  // the way the browser renders it (block element = line, <br> = line break),
  // instead of innerText's string conversion. contenteditable structures mix
  // bare text, <div>-wrapped lines and <br>s (especially after edits), and
  // innerText can emit DOUBLE newlines where the screen shows ONE blank line
  // — which rendered PNGs with bigger gaps than the customer saw.
  // ── Round canvas-png boxes (2026-09-28) ───────────────────────────────────
  // One of the two floated "walls" that make the browser wrap text inside a
  // circle. Each float is half the box wide and full height; its shape-outside
  // polygon is the part of that half lying OUTSIDE the circle (circle radius =
  // box radius minus the safe zone), so line boxes stop at the curve. The browser
  // still does all the wrapping/caret/editing — these only shorten the lines.
  function _makeCircleWall(side, safe) {
    var r = 1 - 2 * safe;           // inner radius in units of the box radius
    var N = 48;
    var pts = [];
    var isLeft = side === 'left';
    pts.push(isLeft ? '0% 0%' : '100% 0%');
    pts.push(isLeft ? '100% 0%' : '0% 0%');
    for (var j = 0; j <= N; j++) {
      var yR = -r + (2 * r * j) / N;                       // -r … r, from the centre
      var half = Math.sqrt(Math.max(0, r * r - yR * yR));  // half-chord (box-radius units)
      var x = isLeft ? (1 - half) : half;                  // wall edge within this float
      var y = (yR + 1) / 2;                                // 0 … 1 of the float height
      pts.push((x * 100).toFixed(3) + '% ' + (y * 100).toFixed(3) + '%');
    }
    pts.push(isLeft ? '100% 100%' : '0% 100%');
    pts.push(isLeft ? '0% 100%' : '100% 100%');
    var shape = 'polygon(' + pts.join(', ') + ')';
    var el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.className = 'arlo-opt-fp-circle-wall';
    el.style.float = side;
    el.style.width = '50%';
    el.style.height = '100%';
    el.style.shapeOutside = shape;
    el.style.webkitShapeOutside = shape;
    el.style.pointerEvents = 'none';
    el.style.userSelect = 'none';
    return el;
  }

  // Snapshot of exactly what the customer sees in a round box: every rendered
  // line (the browser's own wraps included) with its horizontal centre and
  // vertical centre as fractions of the artboard, plus font size and letter
  // spacing as fractions of the artboard height. renderTextToCanvas() paints
  // from this, scaled, instead of re-wrapping — so the PNG equals the screen.
  // `board` is the artboard element (fpFlex, the area inside the round border).
  // Returns null if it can't measure (hidden box etc.) → normal render path.
  function extractCircleLayout(fpEl, board) {
    if (!fpEl || !board || !document.createRange) return null;
    var br = board.getBoundingClientRect();
    if (!br.width || !br.height) return null;
    var range = document.createRange();
    var lines = [];
    // 2026-10-02: lines are built from WHOLE WORDS, never single letters. Round
    // boxes use overflow-wrap:normal, so the browser never breaks inside a word
    // on screen — but per-letter rects aren't reliable at a soft wrap (Safari
    // can report a letter on the wrong line), and grouping letter by letter
    // split the last word of a full line across two lines in the PNG. Now each
    // word goes on the line where most of its letters sit (median y), and only
    // letters that agree with that line count towards its left/right edges.
    var word = [];
    function rectOf(node, i, len) {
      range.setStart(node, i);
      range.setEnd(node, i + len);
      var rs = range.getClientRects(), best = null;
      for (var q = 0; q < rs.length; q++) {
        // Widest wins: skips zero-width caret-like rects at line ends.
        if (rs[q].height > 0 && (!best || rs[q].width > best.width)) best = rs[q];
      }
      return best;
    }
    function flushWord() {
      if (!word.length) return;
      var ys = word.filter(function (g) { return g.rect; })
                   .map(function (g) { return (g.rect.top + g.rect.bottom) / 2; })
                   .sort(function (a, b) { return a - b; });
      if (ys.length) {
        var cy = ys[Math.floor(ys.length / 2)];
        var h = 0;
        word.forEach(function (g) { if (g.rect) h = Math.max(h, g.rect.bottom - g.rect.top); });
        var last = lines[lines.length - 1];
        if (!last || Math.abs(cy - last.cy) > h * 0.5) {
          last = { cy: cy, text: '', l: Infinity, r: -Infinity };
          lines.push(last);
        }
        word.forEach(function (g) {
          last.text += g.ch;
          if (!g.rect) return;
          var gy = (g.rect.top + g.rect.bottom) / 2;
          if (Math.abs(gy - last.cy) > h * 0.5) return; // misreported letter
          if (g.rect.left < last.l) last.l = g.rect.left;
          if (g.rect.right > last.r) last.r = g.rect.right;
        });
      }
      word = [];
    }
    var walker = document.createTreeWalker(fpEl, NodeFilter.SHOW_TEXT, null, false);
    var node;
    while ((node = walker.nextNode())) {
      var v = node.nodeValue;
      for (var i = 0; i < v.length; i++) {
        var c = v.charAt(i);
        if (c === '\n' || c === '\r') { flushWord(); continue; }
        var len = 1;
        var code = v.charCodeAt(i);
        if (code >= 0xD800 && code <= 0xDBFF && i + 1 < v.length) len = 2; // surrogate pair
        var ch = v.substr(i, len);
        var rect = rectOf(node, i, len);
        i += len - 1;
        if (/\s/.test(ch)) {
          flushWord();
          if (lines.length) lines[lines.length - 1].text += ch;
        } else {
          word.push({ ch: ch, rect: rect });
        }
      }
      flushWord(); // text-node boundary (<br>, new <div> line, span edge)
    }
    var st = getComputedStyle(fpEl);
    var fs = parseFloat(st.fontSize) || 0;
    var ls = parseFloat(st.letterSpacing); // 'normal' → NaN
    var out = [];
    lines.forEach(function (ln) {
      var t = ln.text.replace(/^\s+|\s+$/g, '');
      if (!t || !isFinite(ln.l)) return;
      out.push({
        text: t,
        cx: ((ln.l + ln.r) / 2 - br.left) / br.width,
        cy: (ln.cy - br.top) / br.height
      });
    });
    if (!out.length || !fs) return null;
    return { lines: out, fsFrac: fs / br.height, lsFrac: isFinite(ls) ? ls / br.height : 0 };
  }

  function extractVisualLines(root) {
    var lines = [];
    var cur = null; // null = no line currently open
    function put(s) { cur = (cur === null ? '' : cur) + s; }
    function close() { lines.push(cur === null ? '' : cur); cur = null; }
    function walk(node) {
      for (var ch = node.firstChild; ch; ch = ch.nextSibling) {
        if (ch.nodeType === 3) {
          // 2026-08-17: literal \n INSIDE text nodes must count as line breaks
          // too. The input uses white-space:pre-wrap and the canvas-mode paste
          // handler keeps newlines, so browsers (Safari especially) insert
          // pasted line breaks as literal \n text rather than <div>/<br>.
          // Previously "Line1\n" followed by a <div> block produced
          // "Line1\n" + join("\n") = a DOUBLE newline → phantom blank line
          // → double line spacing in the PNG. Splitting here (and NOT opening
          // a phantom line for a trailing empty segment) makes a text-node \n
          // that lands next to a block boundary collapse into one break,
          // matching what pre-wrap actually shows on screen.
          var val = ch.nodeValue.replace(/\r/g, '');
          if (val.indexOf('\n') === -1) {
            put(val);
          } else {
            var parts = val.split('\n');
            for (var pi = 0; pi < parts.length; pi++) {
              if (pi > 0) close();
              if (parts[pi]) put(parts[pi]);
            }
          }
        } else if (ch.nodeName === 'BR') {
          close();
        } else if (ch.nodeType === 1 && /^(DIV|P)$/.test(ch.nodeName)) {
          if (cur !== null) close();       // end any pending inline line
          var before = lines.length;
          walk(ch);
          if (cur !== null) close();       // line content without trailing <br>
          else if (lines.length === before) close(); // completely empty block = one blank line
        } else if (ch.nodeType === 1) {
          walk(ch);                        // inline wrapper (span etc.)
        }
      }
    }
    walk(root);
    if (cur !== null) close();
    // Drop leading/trailing blank lines (matches the old .trim() behaviour
    // without eating intentional blank lines in the middle).
    while (lines.length && lines[0].trim() === '') lines.shift();
    while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
    return lines;
  }

  function drawHeartGlyph(ctx, x, topY, w, h, color) {
    // 2026-08-07: silhouette changed from the plump Twemoji heart to the
    // NARROW classic heart-suit shape (per Damian — the font-style ♥ is the
    // "correct heart"; the plump one only ever appeared on fonts whose stack
    // can't render a monochrome ♥, and looked wrong next to the text).
    // Design box: x 16.4–83.6 (w 67.2), y 6–88 (h 82) — slim lobes, deep
    // pointed taper, ~0.82 width/height ratio like a serif text heart.
    var sx = w / 67.2;
    var sy = h / 82;
    ctx.save();
    ctx.fillStyle = color;
    ctx.translate(x - 16.4 * sx, topY - 6 * sy);
    ctx.scale(sx, sy);
    ctx.beginPath();
    ctx.moveTo(50, 88);
    ctx.bezierCurveTo(50, 88, 16.4, 56, 16.4, 30);
    ctx.bezierCurveTo(16.4, 14, 26, 6, 34.8, 6);
    ctx.bezierCurveTo(42, 6, 47.6, 11, 50, 19);
    ctx.bezierCurveTo(52.4, 11, 58, 6, 65.2, 6);
    ctx.bezierCurveTo(74, 6, 83.6, 14, 83.6, 30);
    ctx.bezierCurveTo(83.6, 56, 50, 88, 50, 88);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // Renders one line starting at (startX, topY) with textAlign effectively 'left',
  // substituting a drawn heart shape for every ♥ instead of fillText-ing it.
  // Returns nothing — paints directly, matching ctx.fillText's own contract.
  // 2026-07-27 position fix: the heart used to hang from the line top with a
  // fixed 0.06em offset (floating visibly ABOVE the baseline next to real
  // glyphs) and advanced with zero side bearings (crowding its neighbours —
  // "♥your"). Now: bottom of the heart sits ON the font's alphabetic baseline
  // (measured from real font metrics, like any letter), with an 0.08em side
  // bearing each side to match normal glyph spacing.
  // 2026-08-07 FINAL positioning: font-reported metrics LIE for display fonts
  // (huge declared bounding boxes put the computed baseline below the real
  // one — hearts rendered hanging under the text). So instead: draw a capital
  // 'H' on a scratch canvas with the SAME font and textBaseline('top') the
  // real render uses, pixel-scan where it actually paints, and anchor the
  // heart to that measured cap band. Reality can't lie. Cached per font
  // string so the scan runs once per font per page.
  var _heartCapCache = {};
  function heartMetrics(ctx, fs) {
    var key = ctx.font;
    var cap = _heartCapCache[key];
    if (!cap) {
      cap = null;
      try {
        var side = Math.ceil(fs * 2.5);
        var c = document.createElement('canvas');
        c.width = side; c.height = side;
        var x = c.getContext('2d');
        x.font = ctx.font;
        x.fillStyle = '#000';
        x.textBaseline = 'top';
        var anchor = Math.round(fs * 0.5);
        x.fillText('H', anchor, anchor);
        var d = x.getImageData(0, 0, side, side).data;
        var minY = -1, maxY = -1;
        for (var row = 0; row < side; row++) {
          for (var col = 0; col < side; col++) {
            if (d[(row * side + col) * 4 + 3] > 40) { if (minY === -1) minY = row; maxY = row; break; }
          }
        }
        if (minY !== -1 && maxY > minY) {
          cap = { top: minY - anchor, bottom: maxY - anchor }; // offsets from the textBaseline='top' anchor
        }
      } catch (e) {}
      if (!cap) cap = { top: Math.round(fs * 0.1), bottom: Math.round(fs * 0.8) }; // metric-free fallback
      _heartCapCache[key] = cap;
    }
    // Heart = 85% of the measured cap height (ratio taken from Damian's
    // approved reference render), suit-silhouette 0.82 width ratio, bottom
    // tip seated on the measured baseline (= bottom of the cap band).
    var capH = cap.bottom - cap.top;
    var h = Math.max(4, Math.round(capH * 0.85));
    return {
      w: Math.round(h * 0.82),
      h: h,
      bearing: Math.round(fs * 0.08),
      capTop: cap.top,
      capBottom: cap.bottom
    };
  }

  // ── Letter spacing on canvas (2026-10-09) ─────────────────────────────────
  // ctx.letterSpacing where the browser has it; otherwise each character is
  // drawn separately with the extra gap, so iPhones without canvas
  // letterSpacing still print the spacing the customer saw on screen.
  function setCanvasSpacing(ctx, px) {
    ctx._arloLS = px || 0;
    ctx._arloLSNative = 'letterSpacing' in ctx;
    if (ctx._arloLSNative) ctx.letterSpacing = ctx._arloLS + 'px';
  }
  function _manualLS(ctx) { return ctx._arloLS && !ctx._arloLSNative ? ctx._arloLS : 0; }
  function spacedWidth(ctx, s) {
    return ctx.measureText(s).width + _manualLS(ctx) * Array.from(s).length;
  }
  function fillSpaced(ctx, s, x, y) {   // honours ctx.textAlign like fillText
    var extra = _manualLS(ctx);
    if (!extra) { ctx.fillText(s, x, y); return; }
    var align = ctx.textAlign, w = spacedWidth(ctx, s);
    var sx = align === 'center' ? x - w / 2 : (align === 'right' || align === 'end') ? x - w : x;
    ctx.textAlign = 'left';
    Array.from(s).forEach(function (ch) { ctx.fillText(ch, sx, y); sx += ctx.measureText(ch).width + extra; });
    ctx.textAlign = align;
  }

  function fillTextWithHeart(ctx, line, startX, topY, fs, color) {
    var hm = heartMetrics(ctx, fs);
    // Bottom tip on the MEASURED baseline (pixel-scanned cap bottom), same
    // seat as the approved reference render.
    var heartTop = Math.round(topY + hm.capBottom - hm.h);
    var cx = startX;
    for (var i = 0; i < line.length; i++) {
      var ch = line[i];
      if (ch === HEART_CHAR) {
        // Hearts ALWAYS render black (per Damian 2026-07-27), regardless of
        // the product's configured text color.
        drawHeartGlyph(ctx, cx + hm.bearing, heartTop, hm.w, hm.h, '#000000');
        cx += hm.bearing + hm.w + hm.bearing + (ctx._arloLS || 0);
      } else {
        ctx.fillText(ch, cx, topY);
        cx += ctx.measureText(ch).width + _manualLS(ctx);
      }
    }
  }

  // Same left-to-right walk as fillTextWithHeart, but just sums widths — used to
  // find the correct starting X for center/right alignment before painting.
  // MUST use the same advance formula as fillTextWithHeart (bearing + w + bearing).
  function measureLineWithHeart(ctx, line, fs) {
    var hm = heartMetrics(ctx, fs);
    var w = 0;
    for (var i = 0; i < line.length; i++) {
      w += (line[i] === HEART_CHAR) ? (hm.bearing + hm.w + hm.bearing + (ctx._arloLS || 0))
                                    : ctx.measureText(line[i]).width + _manualLS(ctx);
    }
    return w;
  }

  // layout (optional, round boxes only) = extractCircleLayout() snapshot of the
  // on-screen lines; when present the PNG is painted from it (no re-wrapping).
  function renderTextToCanvas(text, fontConfig, cs, layout) {
    cs = cs || {};
    var dpi  = cs.dpi  || 300;
    var wMm  = cs.templateWidthMm  || 150;
    var hMm  = cs.templateHeightMm || 50;
    var wPx  = Math.round((wMm / MM_PER_INCH) * dpi);
    var hPx  = Math.round((hMm / MM_PER_INCH) * dpi);
    // fontSize stored in pt; convert to px at the output DPI (1pt = dpi/72 px)
    var fsPt = cs.fontSize;
    var fs   = fsPt ? Math.round(fsPt * dpi / 72) : Math.round(hPx * 0.4);
    var color    = cs.color  || '#000000';
    var vAlign   = cs.vAlign || 'middle';
    var hAlign   = cs.hAlign || 'center';
    var vOffset  = cs.vOffset || 0;
    var kerning  = cs.kerning || 0;
    // Admin stores this as canvasSettings.leading (see admin/public/index.html
    // #p-canvas-leading) — cs.lineHeight was never actually the right key, so this
    // was silently ignoring the admin's Line Spacing setting and always using the
    // 1.25 fallback. Keep .lineHeight as a defensive fallback only.
    var lineHeightMult = cs.leading || cs.lineHeight || 1.25;
    var fontFamily = fontConfig ? '\'' + fontConfig.id + '\'' : 'sans-serif';

    var isCirclePng = cs.shape === 'circle';

    var canvas = document.createElement('canvas');
    canvas.width  = wPx;
    canvas.height = hPx;
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, wPx, hPx);

    // Circle clip — mask to circle for circle artboards
    if (isCirclePng) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(wPx / 2, hPx / 2, Math.min(wPx, hPx) / 2, 0, Math.PI * 2);
      ctx.clip();
    }

    ctx.font      = fs + 'px ' + fontFamily;
    ctx.fillStyle = color;
    ctx.textAlign = hAlign === 'center' ? 'center' : hAlign;

    // Letter Spacing % of the font size; older options: px "kerning" as before.
    setCanvasSpacing(ctx, cs.letterSpacingPct ? cs.letterSpacingPct / 100 * fs : kerning);

    // Round box (2026-09-28): paint each line exactly where the customer saw it
    // on screen — same line breaks, same centre, same height, font size and
    // letter spacing scaled from the on-screen box. No re-wrapping, so the PNG
    // matches the screen and nothing crosses the curve that didn't on screen.
    if (isCirclePng && layout && layout.lines && layout.lines.length) {
      var lfs = layout.fsFrac * hPx;
      ctx.font = lfs + 'px ' + fontFamily;
      setCanvasSpacing(ctx, layout.lsFrac ? layout.lsFrac * hPx : 0); // spacing as seen on screen
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      // The browser centres the font's content area (ascent + descent) on each
      // line; put the canvas content-area centre on the same y.
      var fm = ctx.measureText('Hg');
      var fA = fm.fontBoundingBoxAscent, fD = fm.fontBoundingBoxDescent;
      var hasFB = isFinite(fA) && isFinite(fD) && (fA + fD) > 0;
      layout.lines.forEach(function (ln) {
        var hasHeart = ln.text.indexOf(HEART_CHAR) !== -1;
        var lw = hasHeart ? measureLineWithHeart(ctx, ln.text, lfs) : spacedWidth(ctx, ln.text);
        var startX = ln.cx * wPx - lw / 2;
        var cyPx = ln.cy * hPx;
        var y = hasFB ? cyPx - (fD - fA) / 2 : cyPx - lfs * 0.6;
        if (hasHeart) fillTextWithHeart(ctx, ln.text, startX, y, lfs, color);
        else fillSpaced(ctx, ln.text, startX, y);
      });
      ctx.restore(); // circle clip
      return canvas;
    }

    // 2026-08-07 FINAL heart approach (per Damian, calibrated against his
    // reference renders): ALWAYS use the drawn suit-heart glyph. The previous
    // probe-then-native-glyph path pulled ♥ from whatever FALLBACK font the
    // platform chose (the custom fonts have no ♥), giving a different plump,
    // low-hanging heart per font/device. The drawn glyph is deterministic:
    // same narrow suit silhouette, same size ratio, same baseline seat, on
    // every product and platform. (probeHeartGlyph removed with this change.)

    // ── Word-wrap ────────────────────────────────────────────────────────────
    // The contenteditable div wraps text visually in the browser but innerText
    // only contains explicit \n characters typed by the user (Enter key).
    // We must replicate the wrapping here so long lines break at the canvas edge.
    //
    // Max usable width:
    //   rectangle → full canvas width minus one character of padding each side
    //   circle    → ~82% of diameter (inscribed rectangle that avoids clipped edges)
    ctx.textBaseline = 'top'; // inkExtent() measures relative to this
    var edgePad, maxWidth, lines, lineH;
    function wrapAtCurrentSize() {
      edgePad  = Math.round(fs * 0.5);
      // Circle: wrap text within the safe zone (~90% of diameter) — matches the 5%
      // inset applied to the storefront input box, so page and PNG line up.
      maxWidth = isCirclePng ? Math.round(wPx * 0.90) : wPx - edgePad * 2;
      // Split on explicit newlines first, then word-wrap each segment
      lines = [];
      text.split('\n').forEach(function (hard) {
        if (!hard) { lines.push(''); return; } // preserve intentional blank lines
        var words = hard.split(' ');
        var cur = '';
        words.forEach(function (word) {
          var test = cur ? cur + ' ' + word : word;
          if (spacedWidth(ctx, test) > maxWidth && cur) {
            lines.push(cur);
            cur = word;
          } else {
            cur = test;
          }
        });
        if (cur) lines.push(cur);
      });
      lineH = Math.round(fs * lineHeightMult);
    }
    wrapAtCurrentSize();

    // Where the block starts, as before: centred (or top/bottom), then vOffset.
    function blockTopAtCurrentSize() {
      var totalH = lines.length * lineH, top;
      if (vAlign === 'top') top = edgePad;
      else if (vAlign === 'bottom') top = hPx - totalH - edgePad;
      else top = Math.round((hPx - totalH) / 2);
      return top + vOffset;
    }
    // Actual ink above / below a line's top-baseline y, measured from the first and
    // last lines that have text.
    function inkExtent() {
      var first = 0, last = lines.length - 1;
      while (first < last && !lines[first]) first++;
      while (last > first && !lines[last]) last--;
      var m1 = ctx.measureText(lines[first] || 'H'), m2 = ctx.measureText(lines[last] || 'H');
      return {
        top: first * lineH + (isFinite(m1.actualBoundingBoxAscent) ? -m1.actualBoundingBoxAscent : 0),
        bottom: last * lineH + (isFinite(m2.actualBoundingBoxDescent) ? m2.actualBoundingBoxDescent : fs)
      };
    }
    function fitsCanvas() {
      var top = blockTopAtCurrentSize(), ink = inkExtent();
      if (top + ink.top < 0 || top + ink.bottom > hPx) return false;
      for (var i = 0; i < lines.length; i++) {
        var w = lines[i].indexOf(HEART_CHAR) === -1 ? spacedWidth(ctx, lines[i]) : measureLineWithHeart(ctx, lines[i], fs);
        if (w > wPx) return false;
      }
      return true;
    }
    // Never cut text off (2026-11-04). A message taller than the artboard used to be
    // centred and clipped: 6 lines on the 31x18mm Heart Leather Page Keeper lost the
    // whole first line and half the last, on the printed file. The on-screen overflow
    // guard should stop that, but the screen wraps separately (rounded font size, and
    // whatever font had loaded), so this is the last line of defence: shrink the
    // text just enough to fit. Files that already fit don't change at all.
    var shrunk = false, fsStart = fs;
    while (!isCirclePng && !fitsCanvas() && fs > fsStart * 0.5) {
      shrunk = true;
      fs = Math.max(Math.floor(fs * 0.97), 1);
      ctx.font = fs + 'px ' + fontFamily;
      setCanvasSpacing(ctx, cs.letterSpacingPct ? cs.letterSpacingPct / 100 * fs : kerning);
      wrapAtCurrentSize();
    }
    if (shrunk) console.warn('[ArloOptions] canvas text shrunk to fit: ' + fsStart + 'px -> ' + fs + 'px, ' + lines.length + ' lines');

    // Horizontal anchor
    var x = hAlign === 'center' ? wPx / 2
          : hAlign === 'left'   ? edgePad
          : wPx - edgePad;

    var blockTop = blockTopAtCurrentSize();
    // A shrunk block is placed by its ink so it sits properly inside the artboard.
    if (shrunk && vAlign === 'middle') {
      var inkB = inkExtent();
      blockTop = Math.round((hPx - (inkB.bottom - inkB.top)) / 2 - inkB.top);
    }

    ctx.textBaseline = 'top';
    lines.forEach(function (line, i) {
      var y = blockTop + i * lineH;
      if (line.indexOf(HEART_CHAR) === -1) {
        fillSpaced(ctx, line, x, y);
        return;
      }
      // Paint manually left-to-right (see
      // fillTextWithHeart above) so the heart is drawn as a shape, not routed
      // through font fallback. ctx.textAlign doesn't apply to manual per-
      // character fillText calls, so work out the correct starting X first,
      // matching whatever alignment the rest of this render is using.
      var lineWidth = measureLineWithHeart(ctx, line, fs);
      var startX = hAlign === 'center' ? x - lineWidth / 2
                 : hAlign === 'left'   ? x
                 : x - lineWidth; // right-aligned: x is the right edge
      var prevAlign = ctx.textAlign;
      ctx.textAlign = 'left';
      fillTextWithHeart(ctx, line, startX, y, fs, color);
      ctx.textAlign = prevAlign;
    });

    if (isCirclePng) ctx.restore();

    return canvas;
  }

  // ── PNG Preview overlay ────────────────────────────────────────────────────
  function showPngPreview(canvas, dpi) {
    // Remove any existing overlay
    var old = document.getElementById('arlo-png-preview-overlay');
    if (old) old.remove();

    var wPx = canvas.width;
    var hPx = canvas.height;
    var MM_PER_INCH = 25.4;
    var wMm = Math.round((wPx / dpi) * MM_PER_INCH);
    var hMm = Math.round((hPx / dpi) * MM_PER_INCH);

    var overlay = document.createElement('div');
    overlay.id = 'arlo-png-preview-overlay';
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:99999',
      'background:rgba(0,0,0,0.85)',
      'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center',
      'padding:24px', 'box-sizing:border-box'
    ].join(';');

    // Checkerboard background behind image (shows transparency)
    var imgWrap = document.createElement('div');
    imgWrap.style.cssText = [
      'background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 / 16px 16px',
      'max-width:min(90vw,600px)', 'max-height:60vh',
      'display:flex', 'align-items:center', 'justify-content:center',
      'overflow:hidden', 'border-radius:4px'
    ].join(';');

    var img = document.createElement('img');
    img.src = canvas.toDataURL('image/png');
    img.style.cssText = 'max-width:100%;max-height:60vh;display:block;';
    imgWrap.appendChild(img);

    // Info bar
    var info = document.createElement('div');
    info.style.cssText = 'color:#fff;font-size:13px;margin-top:12px;text-align:center;font-family:-apple-system,sans-serif;opacity:0.8;';
    info.textContent = wPx.toLocaleString() + ' × ' + hPx.toLocaleString() + ' px  ·  ' + wMm + ' × ' + hMm + 'mm  ·  ' + dpi + ' dpi';

    // Buttons row
    var btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:10px;margin-top:16px;';

    var dlBtn = document.createElement('a');
    dlBtn.download = 'preview-' + Date.now() + '.png';
    dlBtn.href = canvas.toDataURL('image/png');
    dlBtn.textContent = 'Download PNG';
    dlBtn.style.cssText = [
      'padding:8px 20px', 'background:#fff', 'color:#111',
      'border-radius:4px', 'font-size:13px', 'font-weight:600',
      'text-decoration:none', 'font-family:-apple-system,sans-serif'
    ].join(';');

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = [
      'padding:8px 20px', 'background:transparent', 'color:#fff',
      'border:1px solid rgba(255,255,255,0.4)', 'border-radius:4px',
      'font-size:13px', 'font-weight:600', 'cursor:pointer',
      'font-family:-apple-system,sans-serif'
    ].join(';');
    closeBtn.addEventListener('click', function () { overlay.remove(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) overlay.remove(); });

    btnRow.appendChild(dlBtn);
    btnRow.appendChild(closeBtn);
    overlay.appendChild(imgWrap);
    overlay.appendChild(info);
    overlay.appendChild(btnRow);
    document.body.appendChild(overlay);
  }

  function canvasToBlob(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob); else reject(new Error('toBlob failed'));
      }, 'image/png');
    });
  }

  function uploadToUploadcare(blob, publicKey, filename) {
    var fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', publicKey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', blob, filename || 'artwork.png');
    return fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.file) throw new Error('No file UUID');
        return 'https://ucarecdn.com/' + data.file + '/';
      });
  }

  function getOrCreateStatusEl() {
    var el = document.getElementById('arlo-canvas-status');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'arlo-canvas-status';
    el.style.cssText = 'display:none;position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1a1a1a;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;font-family:-apple-system,sans-serif;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.3);align-items:center;gap:10px;max-width:90vw';
    document.body.appendChild(el);
    return el;
  }

  function showStatus(el, msg, type) {
    el.style.display = 'flex';
    el.innerHTML = (type === 'loading'
      ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" style="animation:arlo-spin 0.8s linear infinite;flex-shrink:0"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>'
      : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>')
      + '<span>' + msg + '</span>';
    if (!document.getElementById('arlo-spin-style')) {
      var s = document.createElement('style');
      s.id = 'arlo-spin-style';
      s.textContent = '@keyframes arlo-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(s);
    }
  }

  function hideStatus(el) { if (el) el.style.display = 'none'; }

  // ── Get current product handle ────────────────────────────────────────────────
  function getCurrentHandle() {
    var handle = (window.ShopifyAnalytics &&
                  window.ShopifyAnalytics.meta &&
                  window.ShopifyAnalytics.meta.product &&
                  window.ShopifyAnalytics.meta.product.handle) || null;
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m ? m[1] : null;
    }
    return handle;
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    var handle = getCurrentHandle();
    var config = window.ArloProductConfig || {};

    // Only run on products configured in the Arlo admin
    var isArloProduct = handle && (
      (config.products && config.products[handle]) ||
      (config.productOptions && config.productOptions[handle]) ||
      (config.uploadcareVariants && config.uploadcareVariants[handle]) ||
      (config.variantButtons && config.variantButtons[handle])
    );
    if (!isArloProduct) {
      // Remove the class so any scoped suppress CSS stops applying immediately.
      // Critical for AJAX navigation: <head> style element persists across pages.
      document.documentElement.classList.remove('arlo-managed');
      return;
    }

    suppressNativeOptions();

    var options = getOptions();
    if (!options || !options.length) return;

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    injectStyles();
    buildFields(options, form);
    hookForm(options, form);
  }

  function waitForConfig(cb, attempts) {
    attempts = attempts || 0;
    if (window.ArloProductConfig) return cb();
    if (attempts > 30) return;
    setTimeout(function () { waitForConfig(cb, attempts + 1); }, 100);
  }

  // ── Reset all personalisation after successful cart add ───────────────────────
  function resetPersonalisation() {
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    // Clear all hidden property inputs
    container.querySelectorAll('input[type="hidden"]').forEach(function (el) {
      el.value = '';
    });

    // 2026-07-26 fix: the wipe above also cleared each font-previewer's font
    // property (default seeded at build time, re-set only on font button
    // CLICKS) — but the font buttons keep their visual active state through a
    // reset. So on a second add in the same page session the customer sees a
    // font selected, never re-clicks it, and "Font style & size" goes to the
    // cart empty → dropped entirely (empty values are skipped at collect).
    // Symptom: two identical cart lines, first with the font property, second
    // without. Re-seed each font hidden input from the visibly active button.
    // (DOM contract: the font hidden input is appended immediately after its
    // .arlo-opt-fp-fonts row — see the font-previewer builder.)
    container.querySelectorAll('.arlo-opt-fp-fonts').forEach(function (row) {
      var fh = row.nextElementSibling;
      if (!fh || fh.tagName !== 'INPUT' || fh.type !== 'hidden') return;
      var activeName = row.querySelector('.arlo-opt-fp-font-btn.active .arlo-opt-fp-font-name');
      if (activeName && activeName.textContent) fh.value = activeName.textContent;
    });

    // Deselect buttons and tiles
    container.querySelectorAll('.arlo-opt-button, .arlo-opt-tile').forEach(function (el) {
      el.classList.remove('active');
    });

    // Clear text inputs and contenteditable (monogram, font-previewer)
    container.querySelectorAll('input[type="text"], input:not([type])').forEach(function (el) {
      el.value = '';
      el.dispatchEvent(new Event('input'));
    });
    container.querySelectorAll('[contenteditable]').forEach(function (el) {
      el.textContent = '';
      el.dispatchEvent(new Event('input'));
    });

    // Reset selects to first option
    container.querySelectorAll('select').forEach(function (el) {
      el.selectedIndex = 0;
      el.dispatchEvent(new Event('change'));
    });

    // Uncheck checkboxes
    container.querySelectorAll('input[type="checkbox"]').forEach(function (el) {
      el.checked = false;
    });

    // Remove error states
    container.querySelectorAll('.arlo-opt-error').forEach(function (el) {
      el.classList.remove('arlo-opt-error');
    });
    container.querySelectorAll('.arlo-opt-err').forEach(function (el) {
      el.style.display = 'none';
    });

    // Reset linked price delta
    if (_linkedExtraCents !== 0) {
      applyLinkedPriceDelta(-_linkedExtraCents);
    }

    // Reset photo uploader
    if (window.ArloPhotoUploader) {
      window.ArloPhotoUploader.reset();
    }
  }

  document.addEventListener('cart:item-added', resetPersonalisation);

  document.addEventListener('DOMContentLoaded', function () {
    waitForConfig(function () {
      init();
      _loadLiveVariantsForSale();
    });
  });

})();
